import type { Task } from '../types/task'
import type { Lot } from '../types/lot'
import type { FeedLog, FishStockLog, MortalityLog } from '../types/production'
import type { InventoryItem } from '../types/inventory'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { dexieTaskRepo } from '../offline/repos/DexieTaskRepo'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { dexieLotEventRepo } from '../offline/repos/DexieLotEventRepo'
import { dexieFeedLogRepo } from '../offline/repos/DexieFeedLogRepo'
import { dexieProductionTableRepo } from '../offline/repos/DexieProductionTableRepo'
import { dexieInventoryItemRepo } from '../offline/repos/DexieInventoryItemRepo'
import { dexieHarvestLogRepo } from '../offline/repos/DexieHarvestLogRepo'
import { dexieLotCostEventRepo } from '../offline/repos/DexieLotCostEventRepo'
import { dexieMortalityLogRepo } from '../offline/repos/DexieMortalityLogRepo'
import { buildMortalityRecordingRows } from './mortalityRecording'
import { buildMortalityByCauseRows, MORTALITY_CAUSE_LABEL_KEY, NO_CAUSE_LABEL_KEY, readMortalityCause } from './mortalityByCause'
import { en, type TranslationKey } from '../i18n/en'
import { es } from '../i18n/es'
import { resolveBrowserLocale } from '../i18n/locales'
import { dexieWeightSampleLogRepo } from '../offline/repos/DexieWeightSampleLogRepo'
import { dexieFishStockLogRepo } from '../offline/repos/DexieFishStockLogRepo'
import { readWaterJSON } from './waterStorage'
import { listStorageKeys } from './storage'
import { getPresetColumns, PRODUCTION_SPECIES_OPTIONS } from '../data/productionSpeciesPresets'
import type { BiomassDataRow } from './productionUnitTable'
import { loadWaterTableRows, type WaterDataRow } from './waterTable'
import { currentRecords, withoutDeletedLotRecords } from './reportDeletedLots'
import { api } from './api'
import { lotApi } from './lotApi'
import type { ReportDatasetKey, ReportDefinitionV1 } from './reportStorage'
import type { ReportScopeV1 } from './reportScope'
import { applyReportScope, resolveScopeToDate } from './reportScope'
import { computeCurrentBiomassPerLot } from './currentBiomassPerLot'
import { carryForwardLastKnownStock } from './forwardFillStock'
import { computeDisplayedEndOfDayStockByRowId } from './productionEndOfDayStock'
import { computeDailyMortalityRateByRowId } from './dailyMortalityRate'
import { findAvailableLotMatch } from './lotIdentity'
import { scopeLogsToLotBucket } from './biomassLotScoping'
import { formatZonedWeekDisplay, getTimeZoneFromFarm, getZonedIsoDate, getZonedWeekKey, resolveTimeZone } from './timeBucketing'
import { buildLotCostPerKgRows } from './lotCostPerKg'
import { buildLocationIndex, decorateRowsWithLocation } from './reportLocationColumns'
import { applyFxConversionToLotCostPerKgRows, type LotCostPerKgFxRow } from './lotCostPerKgFx'
import { overlayMortalityLogsOnProductionRows } from '../screens/biomass/mortalityLogBridge'
import { overlayWeightSamplesOnProductionRows } from '../screens/biomass/weightSampleLogBridge'
import { overlayFishStockOnProductionRows } from '../screens/biomass/fishStockLogBridge'
import { overlayHarvestOnProductionRows } from '../screens/biomass/harvestLogBridge'
import { overlayLotStageOnProductionRows } from '../screens/biomass/lotStageBridge'
import { computeMeanFultonK, computeWeightCvPercent, type IndividualSampleReading } from './individualSampleMath'
import { computeFcrPlanned, computeProjectedWeeklyFeedKg, computeSgrFromSamples } from './lotGrowthFormulas'
import { buildLotFcrInputs } from './lotFeedConversion'
import { buildStockReconciliation, type CountDifference } from './stockReconciliation'
import { buildDeletedLotAuditCounts, type DeletedLotAuditEntry } from './deletedLotsAudit'
import { loadDeletedLots } from './deletedLots'
import { actorLabel, loadActorNames } from './actorNames'
import { farmDateTimeInputValue } from './farmTime'
import { resolveEffectiveSfrMatrix } from './sfrProfiles'
import { interpolateFromMatrix } from './sfrMatrix'
import { resolveFarmProductionSpeciesKey } from './farmProductionSpecies'
import { selectNearestDatedRow } from './feedKpiSelection'
import type { WizardFarm } from '../types/farmWizard'
import { addDays, projectLot, isLowConfidence, type DatedValue, type LotProjection } from './lotProjection'
import { unitVolumeM3 } from './density'
import { buildCapacityCheck, resolveLotCapacity, type CapacityCheck } from './carryingCapacity'
import { readHarvestTargetWeightG, readProjectionTgc } from './lotProjectionSettings'
import { buildLotExists, isHiddenEmptyDayRow } from './emptyDayRows'
import { farmTimeZone, farmToday } from './farmTime'
import { resolveMeasuringPoint } from './measuringPoint'
import {
  buildHatcheryView,
  estimatedFryWeightG,
  isHatcheryStage,
  readEggArrival,
  readEggsReceived,
  readFlowLpm,
  readHatcheryDefaults,
  readLifeStage,
  readStrain,
  readSupplier,
  stageDate,
  unitVolumeLitres,
  withArrivalTemperatures,
  withMeasuringPointTemperatures,
  type EggArrival,
  type HatcheryView,
  type LotLifeStage
} from './hatchery'
import { dropEmptySameDayDuplicateRows } from './emptyDayRows'

export type ReportRow = Record<string, unknown>

export type DatasetColumn = {
  key: string
  label: string
  /**
   * Marks a column whose value is computed/estimated rather than a raw
   * logged reading (e.g. `feed.fishStockDerived`'s mortality-carried-
   * forward stock) — surfaces this couldn't hide it. Consumers that
   * render report tables use this to visually distinguish the two so an
   * operator can tell "actual reading" from "best estimate" at a glance.
   */
  derived?: boolean
  /**
   * v2.2 findings.md #188: the value depends on the stock, so a report with
   * "Stock reconciliado" shows the reconciled figure and the header says so.
   */
  stockBased?: boolean
  /** Localized one-paragraph help (`reports.columnHelp.<key>`), set by reportLabels. */
  help?: string
}

export type ReportDataset = {
  key: ReportDatasetKey
  label: string
  columns: DatasetColumn[]
  /**
   * v2.2 T3-B-06c: `fetchRows` now accepts an optional `ReportScopeV1`.
   * Most datasets ignore the scope here — the shared `applyReportScope`
   * post-processor in [reportScope.ts](./reportScope.ts) handles date +
   * facility filtering off the emitted row's own fields (`recordedAt`,
   * `farmId`, etc.). The `lotCostPerKg` dataset is different: it emits
   * aggregate rows with no date field, so `applyReportScope`'s date filter
   * no-ops on it. Passing the scope into `fetchRows` lets that dataset
   * filter its input event streams by `recordedAt` BEFORE the roll-up,
   * which is the only way to get a correct "as-of" or "to-date" snapshot.
   * Datasets that emit event-level rows can safely ignore the scope arg.
   */
  fetchRows: (scope?: ReportScopeV1 | null, report?: ReportDefinitionV1 | null) => Promise<ReportRow[]>
}

type WaterRowContext = {
  farmId: string
  productionUnitId: string
}

type FarmWithProductionUnits = {
  id: string
  name?: string
  productionUnits?: Array<{ id: string; name?: string; metadata?: Record<string, unknown> | null }>
}

const readFeedLogMetadataText = (log: FeedLog, key: string): string => {
  const value = log.metadata && typeof log.metadata === 'object' ? (log.metadata as Record<string, unknown>)[key] : undefined
  return typeof value === 'string' ? value.trim() : ''
}

const toFeedEventRow = (log: FeedLog, resolvedLot?: { lotId: string; lot: string }): ReportRow => ({
  id: log.id,
  recordedAt: log.recordedAt,
  farmId: log.farmId,
  productionUnitId: log.productionUnitId,
  lotId: resolvedLot?.lotId ?? '',
  lot: resolvedLot?.lot ?? log.lot ?? '',
  feedKg: log.feedKg ?? '',
  feedType: log.feedType ?? '',
  sfrPercent: log.sfrPercent ?? '',
  biomassKg: log.biomassKg ?? '',
  fcr: log.fcr ?? '',
  notes: log.notes ?? '',
  // findings #82 (Office Plans follow-up): traceability and response per event.
  mixName: readFeedLogMetadataText(log, 'mixName'),
  feedLotNumber: readFeedLogMetadataText(log, 'feedLotNumber'),
  feedingResponse: readFeedLogMetadataText(log, 'feedingResponse'),
  createdAt: log.createdAt ?? '',
  updatedAt: log.updatedAt ?? ''
})

const parseDecimal = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const normalized = trimmed.replace(',', '.')
  const n = Number(normalized)
  if (!Number.isFinite(n)) return null
  return n
}

const buildFeedDailyTotalKey = (parts: {
  farmId: string
  productionUnitId: string
  isoDate: string
  lot: string
}): string => (
  `${parts.farmId}::${parts.productionUnitId}::${parts.isoDate}::${parts.lot}`
)

const toTaskRow = (task: Task): ReportRow => ({
  id: task.id,
  title: task.title,
  description: task.description ?? '',
  completed: task.completed,
  dueAt: task.dueAt ?? '',
  updatedAt: task.updatedAt,
  tags: (task.tags ?? []).join(', ')
})

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

const toOptionalIsoDate = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  // Keep it intentionally strict + sortable.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null
  return trimmed
}

const readWarehouseDates = (item: InventoryItem): { receivedOn: string; expiresOn: string } => {
  const metadata = isPlainObject(item.metadata) ? item.metadata : {}
  const fallbackReceived = typeof item.createdAt === 'string' && item.createdAt.length >= 10
    ? item.createdAt.slice(0, 10)
    : ''

  const receivedOn = toOptionalIsoDate(metadata.receivedOn) ?? fallbackReceived
  const expiresOn = toOptionalIsoDate(metadata.expiresOn) ?? ''

  return { receivedOn, expiresOn }
}

const toInventoryRow = (item: InventoryItem): ReportRow => ({
  ...readWarehouseDates(item),
  id: item.id,
  farmId: item.farmId,
  name: item.name,
  category: item.category,
  unit: item.unit,
  onHand: item.onHand,
  reorderPoint: item.reorderPoint ?? '',
  usageFeedType: item.usageFeedType ?? '',
  notes: item.notes ?? '',
  createdAt: item.createdAt,
  updatedAt: item.updatedAt
})

const toLotRow = (lot: Lot): ReportRow => ({
  // v2.2 T3-A-05 (2026-07-06): Lot biology cache fields (`weight`,
  // `dailyMortality`, `fishStock`, `health`, `healthDetail`, `recordDate`)
  // retired from the 'lots' dataset. Operators who need real-time daily
  // biology values for a lot should use the 'productionDatatable' dataset
  // which reads through the WSL / MortalityLog / FishStockLog overlays
  // (T3-A-03 bridge + T3-C-02 wiring). This dataset now exposes lot
  // identity, lifecycle, and lineage fields only. See
  // docs/mvp-2-2-architecture-cleanup-plan.md T3-A-05.
  id: lot.id,
  lotId: lot.id,
  lotKey: lot.lotKey,
  farmId: lot.farmId,
  productionUnitId: lot.productionUnitId,
  species: lot.species ?? '',
  stage: lot.stage,
  stockingDate: lot.stockingDate ?? '',
  harvestDate: lot.harvestDate ?? '',
  biomassKg: lot.biomassKg ?? null,
  updatedAt: lot.updatedAt ?? '',
  createdAt: lot.createdAt ?? ''
})

const buildProductionPresetColumnKeys = (): string[] => {
  const all = new Set<string>()
  PRODUCTION_SPECIES_OPTIONS.forEach((option) => {
    getPresetColumns(option.key).forEach((key) => all.add(key))
  })
  return Array.from(all)
}

const PRODUCTION_PRESET_COLUMN_KEYS = buildProductionPresetColumnKeys()

const toProductionRow = (row: BiomassDataRow, sourceKey: string): ReportRow => ({
  sourceKey,
  ...row
})

/**
 * v2.2 findings.md #47: row-level biomass (kg) = derived end-of-day stock ×
 * that row's average weight (g) / 1000. A per-row product can't be built
 * from pivot aggregations, so it's emitted here; aggregate it across lots
 * with `sumOfLast` (it's a level, like stock). Only on rows that carry both
 * a stock and a weight — no weight carry-forward, so days without a weight
 * sample stay null rather than guessing a stale weight. A confirmed `0`
 * stock yields `0` biomass.
 */
export const computeBiomassKgDerived = (stock: number | null, weight: unknown): number | null => {
  if (stock === null || !Number.isFinite(stock)) return null
  const grams = typeof weight === 'number'
    ? weight
    : (typeof weight === 'string' && weight.trim() ? Number(weight.trim().replace(',', '.')) : Number.NaN)
  if (!Number.isFinite(grams)) return null
  return Number(((stock * grams) / 1000).toFixed(3))
}

/**
 * v2.2 findings.md #26: shared by the `feed` and `productionDatatable`
 * datasets. The mortality-aware end-of-day stock (the same derivation
 * Feed.tsx/Biomass.tsx/Datatable.tsx already use for their own KPIs/
 * exports), carried forward across each lot's own chronological history so
 * every row gets a value — not just the exact-date row a stock count landed
 * on (I-6). Extracted once a second dataset needed it, rather than
 * duplicating it a second time — this codebase treats a duplicated per-lot
 * derivation as its own bug class (see v2.2 findings.md #4).
 *
 * Returns `null` for a row whose lot has no stock signal at all yet as of
 * that row (genuinely unknown) versus a number — including a confirmed `0`
 * for a fully depleted lot — once the lot has had at least one stock event.
 * Callers must not collapse `0` into `null` (v2.2 findings.md #31).
 */
const buildFishStockDerivedByRowId = (entries: Array<{ row: BiomassDataRow }>): Map<string, number | null> => {
  const endOfDayStock = computeDisplayedEndOfDayStockByRowId(entries.map(({ row }) => row))

  const byLot = new Map<string, Array<{ row: BiomassDataRow }>>()
  entries.forEach((entry) => {
    const lotId = String(entry.row.lotId ?? '').trim()
    const lotKey = lotId ? `id:${lotId}` : `label:${String(entry.row.lot ?? '').trim().toLowerCase()}`
    if (lotKey === 'label:') return
    const list = byLot.get(lotKey)
    if (list) list.push(entry)
    else byLot.set(lotKey, [entry])
  })

  const derivedStockByRowId = new Map<string, number | null>()
  byLot.forEach((lotEntries) => {
    const sorted = lotEntries.slice().sort((a, b) => (
      String(a.row.date ?? '').localeCompare(String(b.row.date ?? ''))
    ))
    const effectiveStocks = carryForwardLastKnownStock(
      sorted,
      // `numericValue` is `null` only for `source: 'unknown'` rows (no stock
      // signal at all); a `stored`/`derived` row's `numericValue` is a real
      // count, including a genuine `0` for a fully depleted lot, which must
      // not be treated as "no signal" (v2.2 findings.md #31).
      (entry) => endOfDayStock.get(entry.row.id)?.numericValue ?? null
    )
    sorted.forEach((entry, index) => {
      derivedStockByRowId.set(entry.row.id, effectiveStocks[index])
    })
  })

  return derivedStockByRowId
}

const stripProductionEntity = (value: unknown): BiomassDataRow | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const entity = value as Record<string, unknown>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pk, farmId, productionUnitId, updatedAt, ...row } = entity
  return row as BiomassDataRow
}

const toSourceKeyFromContext = (farmId: string, productionUnitId: string): string => (
  `${farmId}::${productionUnitId}`
)

type OverlaidProductionEntry = {
  sourceKey: string
  row: BiomassDataRow
}

/**
 * v2.2 T3-C-02c (2026-07-13): shared helper that loads every production
 * entity and applies the full biomass overlay chain scoped per
 * `(farmId, productionUnitId)` bucket. Consumers get rows whose
 * `weight` / `dailyMortality` / `fishStock` / `harvestKg` / `stage`
 * cells match what the operator sees on Biomass / Datatable in the
 * live app (via `samplingRows` there). Required by invariant I-6 —
 * every surface rendering these cells MUST consume overlay-composed
 * rows, not raw Dexie snapshots. Prior to this helper the `feed` and
 * `productionDatatable` datasets emitted raw entities directly,
 * silently dropping any event-derived value that lived in the Tier A
 * streams (MortalityLog / WeightSampleLog / FishStockLog / HarvestLog).
 *
 * Scope isolation matters: bucketing per (farmId, productionUnitId)
 * prevents cross-contamination when two farms share a `lot` label —
 * the overlay match keys are `(row.date, row.lot)`, which is safe
 * INSIDE a single-context Biomass screen but would collide across the
 * whole tenant if we handed the overlay every log at once.
 */
const loadOverlaidProductionEntitiesBySourceKey = async (): Promise<OverlaidProductionEntry[]> => {
  await ensureOfflineReady()
  const [entities, allMortality, allWeight, allFishStock, allHarvest, allLots] = await Promise.all([
    currentRecords(dexieProductionTableRepo.listAllEntities()),
    currentRecords(dexieMortalityLogRepo.list()).catch(() => []),
    currentRecords(dexieWeightSampleLogRepo.list()).catch(() => []),
    currentRecords(dexieFishStockLogRepo.list()).catch(() => []),
    currentRecords(dexieHarvestLogRepo.list()).catch(() => []),
    dexieLotRepo.list().catch(() => [])
  ])

  type Bucket = {
    farmId: string
    productionUnitId: string
    sourceKey: string
    rows: BiomassDataRow[]
    lotIds: Set<string>
    entryPositions: number[]
  }

  const buckets = new Map<string, Bucket>()
  const entries: OverlaidProductionEntry[] = []
  const attributableLotIds = new Set<string>()

  entities.forEach((entity) => {
    const row = stripProductionEntity(entity)
    if (!row) return
    const farmId = String((entity as any).farmId ?? '')
    const productionUnitId = String((entity as any).productionUnitId ?? '')
    const bucketKey = `${farmId}::${productionUnitId}`
    let bucket = buckets.get(bucketKey)
    if (!bucket) {
      bucket = {
        farmId,
        productionUnitId,
        sourceKey: toSourceKeyFromContext(farmId, productionUnitId),
        rows: [],
        lotIds: new Set<string>(),
        entryPositions: []
      }
      buckets.set(bucketKey, bucket)
    }
    // Reserve the output slot now; we fill `row` after the overlay pass
    // so the caller's iteration order matches Dexie insertion order.
    entries.push({ sourceKey: bucket.sourceKey, row })
    bucket.rows.push(row)
    bucket.entryPositions.push(entries.length - 1)

    const rowLotId = String((row as { lotId?: unknown }).lotId ?? '').trim()
    if (rowLotId) {
      bucket.lotIds.add(rowLotId)
      attributableLotIds.add(rowLotId)
    }
  })

  buckets.forEach((bucket) => {
    // A1.1 Risk #2 fix: scope by lotId first, location only as fallback,
    // so a transferred lot's logs still overlay onto the rows it left
    // behind in its previous location bucket. See `scopeLogsToLotBucket`.
    const scopedMortality = scopeLogsToLotBucket(allMortality, bucket, attributableLotIds)
    const scopedWeight = scopeLogsToLotBucket(allWeight, bucket, attributableLotIds)
    const scopedFishStock = scopeLogsToLotBucket(allFishStock, bucket, attributableLotIds)
    const scopedHarvest = scopeLogsToLotBucket(allHarvest, bucket, attributableLotIds)

    // A1.1 fix (docs/architecture-concerns.md §A1.1, Hallazgo D4/"scopedLots"):
    // `overlayLotStageOnProductionRows` matches purely by `row.lotId ===
    // lot.id` (see src/screens/biomass/lotStageBridge.ts) — it does NOT
    // use farmId/productionUnitId at all. Scoping `allLots` down to the
    // bucket's CURRENT (farmId, productionUnitId) silently dropped the
    // lot-stage overlay for every bucket holding a transferred/moved
    // lot's PRE-move rows, since a `Lot` record only carries its current
    // location — the bucket for the old location would never contain
    // that lot and the overlay match would always miss. Pass every lot
    // through; the overlay's own `row.lotId` map keeps this scoped
    // correctly per row regardless of which bucket a row lives in.
    const overlaid = overlayLotStageOnProductionRows(
      overlayHarvestOnProductionRows(
        overlayFishStockOnProductionRows(
          overlayWeightSamplesOnProductionRows(
            overlayMortalityLogsOnProductionRows(dropEmptySameDayDuplicateRows(bucket.rows), scopedMortality),
            scopedWeight
          ),
          scopedFishStock
        ),
        scopedHarvest
      ),
      allLots
    )

    bucket.entryPositions.forEach((entryIndex, bucketIndex) => {
      const overlaidRow = overlaid[bucketIndex]
      if (overlaidRow) entries[entryIndex] = { sourceKey: bucket.sourceKey, row: overlaidRow }
    })
  })

  return entries
}

// Lot projections (findings #82): one shared loader for the Lotes panel and
// the "Proyección de lotes" dataset, so both show the same figures (I-6).
export type LotProjectionRow = {
  lotId: string
  lotKey: string
  farmId: string
  productionUnitId: string
  /** "Today" in the farm's time zone (YYYY-MM-DD). */
  today: string
  projection: LotProjection
}

const PROJECTION_MORTALITY_WINDOW_DAYS = 30

// One lot's series, shared by the projection and the hatchery view
// (Hatchery v0, findings #84): overlay-corrected production rows, the same
// stock derivation as the other datasets, the unit's water readings.
type LotSeries = {
  lot: Lot
  lots: Lot[]
  farm: FarmWithProductionUnits | undefined
  productionUnitId: string
  today: string
  samplings: DatedValue[]
  stock: number | null
  /** Deaths per calendar day, on days that carry a mortality record (all days). */
  deathsByDay: Map<string, number>
  temperatures: DatedValue[]
  latestFlow: DatedValue | null
}

const parseSeriesNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || !value.trim()) return null
  const parsed = Number.parseFloat(value.trim().replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

// Web-typed rows (Agua, Base de datos) store a calendar day as UTC midnight;
// converting that to the farm's zone west of UTC lands on the previous day
// (the #82 display fix, applied to the series). Crew rows carry the real
// instant and are converted. Found in the Hatchery v0 live check (#84).
export const seriesDay = (value: unknown, timeZone: string): string | null => {
  const raw = String(value ?? '')
  if (/^\d{4}-\d{2}-\d{2}(T00:00(:00(\.0+)?)?Z)?$/.test(raw)) return raw.slice(0, 10)
  return getZonedIsoDate(raw, timeZone)
}

/** A unit's water rows (Agua) and its temperature series by farm day. */
const loadUnitWaterSeries = (
  farm: FarmWithProductionUnits | undefined,
  farmId: string,
  productionUnitId: string,
  timeZone: string
) => {
  const farmName = typeof farm?.name === 'string' ? farm.name : ''
  const unit = farm?.productionUnits?.find((candidate) => candidate.id === productionUnitId)
  const waterRows = loadWaterTableRows({
    farmId,
    productionUnitId,
    farmName,
    productionUnitName: unit?.name || productionUnitId
  })
  const temperatures: DatedValue[] = waterRows
    .filter((row) => row.temperature !== null && row.temperature !== undefined && Number.isFinite(row.temperature))
    .map((row) => ({ date: seriesDay(row.date, timeZone) ?? '', value: Number(row.temperature) }))
    .filter((point) => point.date)
  return { waterRows, temperatures }
}

const loadActiveLotSeries = async (options?: { lotIds?: string[] }): Promise<LotSeries[]> => {
  await ensureOfflineReady()
  const [entries, lots, farms, mortalityLogs] = await Promise.all([
    loadOverlaidProductionEntitiesBySourceKey(),
    dexieLotRepo.list().catch(() => [] as Lot[]),
    dexieFarmRepo.list().catch(() => []) as Promise<FarmWithProductionUnits[]>,
    currentRecords(dexieMortalityLogRepo.list()).catch(() => [])
  ])
  const mortalityLogsByLotId = new Map<string, MortalityLog[]>()
  mortalityLogs.forEach((log) => {
    if (!log.lotId) return
    const list = mortalityLogsByLotId.get(log.lotId)
    if (list) list.push(log)
    else mortalityLogsByLotId.set(log.lotId, [log])
  })
  const wanted = options?.lotIds ? new Set(options.lotIds) : null
  const activeLots = lots.filter((lot) => (
    (!lot.stage || lot.stage === 'Active')
    && lot.productionUnitId
    && (!wanted || wanted.has(lot.id))
  ))
  if (activeLots.length === 0) return []

  const lotsByContext = buildLotsByContext(lots)
  const lotsById = buildLotsById(lots)
  const farmsById = new Map(farms.map((farm) => [farm.id, farm]))

  const entriesByLotId = new Map<string, Array<{ row: BiomassDataRow }>>()
  entries.forEach(({ sourceKey, row }) => {
    const [farmId, productionUnitId] = sourceKey.split('::')
    const resolved = resolveDatasetLotReference({ farmId, productionUnitId, lotId: row.lotId, lot: row.lot, lotsByContext, lotsById })
    if (!resolved.lotId) return
    const list = entriesByLotId.get(resolved.lotId)
    if (list) list.push({ row })
    else entriesByLotId.set(resolved.lotId, [{ row }])
  })

  return activeLots.map((lot) => {
    const farm = farmsById.get(lot.farmId)
    const productionUnitId = String(lot.productionUnitId)
    const timeZone = resolveTimeZone(getTimeZoneFromFarm(farm))
    const today = getZonedIsoDate(new Date(), timeZone) ?? new Date().toISOString().slice(0, 10)
    const lotEntries = (entriesByLotId.get(lot.id) ?? [])
      .slice()
      .sort((a, b) => String(a.row.date ?? '').localeCompare(String(b.row.date ?? '')))

    const samplings: DatedValue[] = []
    const deathsByDay = new Map<string, number>()
    lotEntries.forEach(({ row }) => {
      const day = seriesDay(row.date, timeZone)
      if (!day) return
      const weight = parseSeriesNumber(row.weight)
      if (weight !== null && weight > 0) samplings.push({ date: day, value: weight })
      // findings #74: a day counts only when it carries a mortality record
      // ("Sin mortalidad" = recorded 0); blank and "No se pudo revisar" days do not.
      const deaths = parseSeriesNumber(row.dailyMortality)
      if (deaths !== null && deaths >= 0 && day <= today) deathsByDay.set(day, (deathsByDay.get(day) ?? 0) + deaths)
    })

    // Deaths come from the lot's mortality records themselves: a late entry
    // ("Ayer", "Hace 2 días") for a day with no production row is otherwise
    // lost by the row overlay (found in the Hatchery v0 live check, #84).
    // Same day rule as "Registro de mortalidad": "No se pudo revisar" is not
    // a recorded day, "Sin mortalidad" is a recorded 0. Days without any
    // record keep the row value (older web-typed rows).
    const logDeathsByDay = new Map<string, number>()
    ;(mortalityLogsByLotId.get(lot.id) ?? []).forEach((log) => {
      const metadata = (log.metadata ?? {}) as Record<string, unknown>
      if (metadata.notChecked === true) return
      const day = seriesDay(log.recordedAt, timeZone)
      const count = parseSeriesNumber(log.mortalityCount)
      if (!day || day > today || count === null || count < 0) return
      logDeathsByDay.set(day, (logDeathsByDay.get(day) ?? 0) + count)
    })
    logDeathsByDay.forEach((count, day) => deathsByDay.set(day, count))

    const stockByRowId = buildFishStockDerivedByRowId(lotEntries)
    let stock: number | null = null
    for (let index = lotEntries.length - 1; index >= 0; index -= 1) {
      const value = stockByRowId.get(lotEntries[index].row.id)
      if (value !== null && value !== undefined) {
        stock = value
        break
      }
    }

    const { waterRows, temperatures } = loadUnitWaterSeries(farm, lot.farmId, productionUnitId, timeZone)
    // Hatchery v0: the unit's latest flow reading (L/min, WaterLog.metadata.flowLpm).
    const latestFlowRow = waterRows
      .filter((row) => readFlowLpm(row) !== null)
      .sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')))
      .pop()
    const latestFlowDay = latestFlowRow ? seriesDay(latestFlowRow.date, timeZone) : null
    const latestFlowValue = latestFlowRow ? readFlowLpm(latestFlowRow) : null
    const latestFlow = latestFlowDay && latestFlowValue !== null ? { date: latestFlowDay, value: latestFlowValue } : null

    return { lot, lots, farm, productionUnitId, today, samplings, stock, deathsByDay, temperatures, latestFlow }
  })
}

/**
 * Hatchery v0: a lot's egg-origin record. The lot's own record first; a lot
 * split off an egg lot later (fry tanks) reads its lineage root's, so the
 * egg origin follows the fish (GLOBALG.A.P. 20.01.01 / 20.01.03).
 */
export const resolveHatcherySource = (lot: Lot, lots: Lot[]): Lot => {
  const hasRecord = (candidate: Lot) => Boolean(readLifeStage(candidate)) || Object.keys(readEggArrival(candidate)).length > 0
  if (hasRecord(lot)) return lot
  const root = lot.lineageRootId ? lots.find((candidate) => candidate.id === lot.lineageRootId) : undefined
  return root && hasRecord(root) ? root : lot
}

export const loadLotProjections = async (options?: { lotIds?: string[] }): Promise<LotProjectionRow[]> => {
  const series = await loadActiveLotSeries(options)
  return series.map(({ lot, lots, farm, productionUnitId, today, samplings, stock, deathsByDay, temperatures }) => {
    // Hatchery v0: eggs and yolk-sac alevins neither eat nor grow — no
    // projection. From first feeding, growth starts from the estimated fry
    // weight until the lot's first real sampling (user decision 2026-09-29).
    const source = resolveHatcherySource(lot, lots)
    const lifeStage = readLifeStage(source)
    const firstFeedingDate = stageDate(source, 'firstFeeding')

    // The mortality window never starts before stocking — nor, for an egg
    // lot, before first feeding: egg pick-offs are not fish mortality.
    const windowStartByDays = new Date(Date.parse(`${today}T00:00:00Z`) - (PROJECTION_MORTALITY_WINDOW_DAYS - 1) * 86_400_000)
      .toISOString().slice(0, 10)
    const stockingDay = lot.stockingDate ? String(lot.stockingDate).slice(0, 10) : ''
    const windowFloor = [stockingDay, firstFeedingDate ?? ''].sort().pop() ?? ''
    const windowStart = windowFloor && windowFloor > windowStartByDays ? windowFloor : windowStartByDays
    const windowDays = Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${windowStart}T00:00:00Z`)) / 86_400_000) + 1)
    const mortalityRecordedDays = [...deathsByDay.entries()]
      .filter(([date]) => date >= windowStart && date <= today)
      .map(([date, value]) => ({ date, value }))
    const lotSamplings = firstFeedingDate ? samplings.filter((point) => point.date >= firstFeedingDate) : samplings
    const fryWeight = lifeStage === 'firstFeeding' && firstFeedingDate
      ? estimatedFryWeightG(readEggArrival(source), readHatcheryDefaults(farm as { metadata?: Record<string, unknown> } | undefined).fryWeightFactor)
      : null
    // No fish count yet: eggs received minus every death recorded since arrival.
    const eggsReceived = lifeStage ? readEggsReceived(lot) : null
    const hatcheryStock = eggsReceived !== null
      ? Math.max(0, eggsReceived - [...deathsByDay.entries()]
        .filter(([date]) => !stockingDay || date >= stockingDay)
        .reduce((sum, [, deaths]) => sum + deaths, 0))
      : null

    const species = resolveFarmProductionSpeciesKey(farm as unknown as WizardFarm) ?? 'global'
    const matrix = resolveEffectiveSfrMatrix({
      farm: farm as unknown as WizardFarm | undefined,
      lot,
      species: species as ReturnType<typeof resolveFarmProductionSpeciesKey> & string
    })
    const hasMatrix = matrix.weights.length > 0 && matrix.temperatures.length > 0 && matrix.values.length > 0

    const projection = projectLot({
      samplings: lotSamplings,
      temperatures,
      stock: stock ?? hatcheryStock,
      mortalityRecordedDays,
      mortalityWindowDays: windowDays,
      targetWeightG: readHarvestTargetWeightG(lot) ?? readHarvestTargetWeightG(farm as { metadata?: Record<string, unknown> } | undefined),
      officeTgc: readProjectionTgc(farm as { metadata?: Record<string, unknown> } | undefined),
      sfrAt: (weightG, temperatureC) => (hasMatrix
        ? interpolateFromMatrix(matrix, weightG, temperatureC, { species }).sfr
        : null),
      today,
      estimatedStartWeight: fryWeight !== null && firstFeedingDate ? { date: firstFeedingDate, value: fryWeight } : null,
      hatcheryStage: isHatcheryStage(lifeStage)
    })

    return { lotId: lot.id, lotKey: lot.lotKey, farmId: lot.farmId, productionUnitId, today, projection }
  })
}

export type LotHatcheryRow = {
  lotId: string
  lotKey: string
  farmId: string
  productionUnitId: string
  lifeStage: LotLifeStage
  /** "Today" in the farm's time zone (YYYY-MM-DD). */
  today: string
  arrivalDate: string | null
  hatchDate: string | null
  firstFeedingDate: string | null
  /** The lot the egg-origin record is read from (the lot itself, or its lineage root). */
  sourceLotId: string
  arrival: EggArrival
  supplier: string | null
  strain: string | null
  /** Other Active lots of the same origin (same lineage root) not yet at first feeding. */
  siblingLotIds: string[]
  /** Units of the same arrival whose readings filled days without one here (sorted names). */
  temperatureBorrowedFrom: string[]
  /** Findings #151: the Capacidad de carga minimum L/min per 1,000 and its table; null = none. */
  minFlowPerThousandLpm: number | null
  capacityTableName: string | null
  view: HatcheryView
}

export type LotCapacityCheckRow = {
  lotId: string
  lotKey: string
  farmId: string
  productionUnitId: string
  today: string
  /** Capacidad de carga table applied (the lot's copy, else the facility default); null = none. */
  tableName: string | null
  tableSource: 'lot' | 'facility' | null
  hatchery: boolean
  temperatureC: number | null
  temperatureDate: string | null
  flowLpm: number | null
  flowDate: string | null
  check: CapacityCheck
}

/**
 * Findings #151: today's density and water flow per active lot against its
 * Capacidad de carga table — the unit's latest flow (Agua) and temperature,
 * the lot's stock and latest weight (eggs: received − deaths).
 */
export const loadLotCapacityChecks = async (options?: { lotIds?: string[] }): Promise<LotCapacityCheckRow[]> => {
  const series = await loadActiveLotSeries(options)
  return series.map(({ lot, lots, farm, productionUnitId, today, samplings, stock, deathsByDay, temperatures, latestFlow }) => {
    const lotsById = new Map(lots.map((candidate) => [candidate.id, candidate]))
    const capacity = resolveLotCapacity(farm as { metadata?: Record<string, unknown> } | undefined, lot, lotsById)
    const unit = farm?.productionUnits?.find((candidate) => candidate.id === productionUnitId) as
      { type?: string; capacity?: unknown; capacityUnit?: unknown } | undefined
    const hatchery = isHatcheryStage(readLifeStage(resolveHatcherySource(lot, lots)))
    const stockingDay = lot.stockingDate ? String(lot.stockingDate).slice(0, 10) : ''
    const received = readEggsReceived(lot)
    const deaths = [...deathsByDay.entries()]
      .filter(([day]) => !stockingDay || day >= stockingDay)
      .reduce((sum, [, value]) => sum + value, 0)
    const count = hatchery && received ? Math.max(0, received - deaths) : stock
    const byDate = (a: DatedValue, b: DatedValue) => a.date.localeCompare(b.date)
    const lastWeight = [...samplings].sort(byDate).pop() ?? null
    const lastTemperature = [...temperatures].sort(byDate).pop() ?? null
    return {
      lotId: lot.id,
      lotKey: lot.lotKey,
      farmId: lot.farmId,
      productionUnitId,
      today,
      tableName: capacity?.table.name ?? null,
      tableSource: capacity?.source ?? null,
      hatchery,
      temperatureC: lastTemperature?.value ?? null,
      temperatureDate: lastTemperature?.date ?? null,
      flowLpm: latestFlow?.value ?? null,
      flowDate: latestFlow?.date ?? null,
      check: buildCapacityCheck({
        table: capacity?.table ?? null,
        unitType: unit?.type,
        volumeM3: unitVolumeM3(unit?.capacity, unit?.capacityUnit),
        hatchery,
        stock: count,
        averageWeightG: lastWeight?.value ?? null,
        temperatureC: lastTemperature?.value ?? null,
        flowLpm: latestFlow?.value ?? null
      })
    }
  })
}

/** Hatchery v0 (findings #84): TUA, milestones, survival, density and flow per egg lot. */
export const loadLotHatcheryViews = async (options?: { lotIds?: string[] }): Promise<LotHatcheryRow[]> => {
  const series = await loadActiveLotSeries(options)
  const rows: LotHatcheryRow[] = []
  series.forEach(({ lot, lots, farm, productionUnitId, today, deathsByDay, temperatures, latestFlow }) => {
    const source = resolveHatcherySource(lot, lots)
    const lifeStage = readLifeStage(source)
    if (!lifeStage) return
    const arrival = readEggArrival(source)
    const arrivalDate = lot.stockingDate ? String(lot.stockingDate).slice(0, 10) : stageDate(source, 'eggs')
    const hatchDate = stageDate(source, 'yolkSac')
    const firstFeedingDate = stageDate(source, 'firstFeeding')
    const unit = farm?.productionUnits?.find((candidate) => candidate.id === productionUnitId) as
      { capacity?: unknown; capacityUnit?: unknown } | undefined
    const rootId = lot.lineageRootId ?? lot.id
    const capacity = resolveLotCapacity(farm as { metadata?: Record<string, unknown> } | undefined, lot, new Map(lots.map((candidate) => [candidate.id, candidate])))
    const timeZone = resolveTimeZone(getTimeZoneFromFarm(farm))
    const otherUnitIds = new Set(lots
      .filter((candidate) => (
        candidate.id !== lot.id
        && (candidate.lineageRootId ?? candidate.id) === rootId
        && candidate.farmId === lot.farmId
        && (!candidate.stage || candidate.stage === 'Active')
        && !(candidate as { deletedAt?: string }).deletedAt
        && Boolean(candidate.productionUnitId)
        && candidate.productionUnitId !== productionUnitId
      ))
      .map((candidate) => String(candidate.productionUnitId)))
    const farmUnits = farm?.productionUnits ?? []
    const measuringPoint = resolveMeasuringPoint(farmUnits.find((candidate) => candidate.id === productionUnitId), farmUnits)
    const arrivalTemperatures = withMeasuringPointTemperatures(
      withArrivalTemperatures(
        temperatures,
        [...otherUnitIds].map((unitId) => ({
          unitName: farmUnits.find((candidate) => candidate.id === unitId)?.name || unitId,
          temperatures: loadUnitWaterSeries(farm, lot.farmId, unitId, timeZone).temperatures
        }))
      ),
      measuringPoint && measuringPoint.id !== productionUnitId && !otherUnitIds.has(measuringPoint.id)
        ? { unitName: measuringPoint.name || measuringPoint.id, temperatures: loadUnitWaterSeries(farm, lot.farmId, measuringPoint.id, timeZone).temperatures }
        : null
    )
    const view = buildHatcheryView({
      stage: lifeStage,
      arrivalDate,
      arrival,
      eggsReceived: readEggsReceived(lot),
      hatchDate,
      firstFeedingDate,
      defaults: readHatcheryDefaults(farm as { metadata?: Record<string, unknown> } | undefined),
      temperatures: arrivalTemperatures.temperatures,
      borrowedTemperatureDays: arrivalTemperatures.borrowedDays,
      mortalityRecordedDays: [...deathsByDay.entries()].map(([date, value]) => ({ date, value })),
      latestFlowLpm: latestFlow,
      unitVolumeLitres: unitVolumeLitres(unit),
      today
    })
    const siblingLotIds = lots
      .filter((candidate) => (
        candidate.id !== lot.id
        && (candidate.lineageRootId ?? candidate.id) === rootId
        && (!candidate.stage || candidate.stage === 'Active')
        && Boolean(candidate.productionUnitId)
        && !(candidate as { deletedAt?: string }).deletedAt
        && readLifeStage(candidate) !== 'firstFeeding'
      ))
      .map((candidate) => candidate.id)
    rows.push({
      lotId: lot.id,
      lotKey: lot.lotKey,
      farmId: lot.farmId,
      productionUnitId,
      lifeStage,
      today,
      arrivalDate,
      hatchDate,
      firstFeedingDate,
      sourceLotId: source.id,
      arrival,
      supplier: readSupplier(source),
      strain: readStrain(source),
      siblingLotIds,
      temperatureBorrowedFrom: view.temperatureDaysBorrowed > 0 ? arrivalTemperatures.borrowedFrom : [],
      minFlowPerThousandLpm: capacity?.table.hatcheryMinLpmPerThousand ?? null,
      capacityTableName: capacity?.table.name ?? null,
      view
    })
  })
  return rows
}

const buildLotContextKey = (farmId: string, productionUnitId?: string): string => (
  `${farmId}::${productionUnitId}`
)

const buildLotsByContext = (lots: Lot[]): Map<string, Array<{ id: string; lotKey: string }>> => {
  const byContext = new Map<string, Array<{ id: string; lotKey: string }>>()

  lots.forEach((lot) => {
    const key = buildLotContextKey(lot.farmId, lot.productionUnitId)
    const entry = byContext.get(key) ?? []
    entry.push({ id: lot.id, lotKey: lot.lotKey })
    byContext.set(key, entry)
  })

  return byContext
}

const buildLotsById = (lots: Lot[]): Map<string, { id: string; lotKey: string }> => {
  const byId = new Map<string, { id: string; lotKey: string }>()

  lots.forEach((lot) => {
    if (!lot?.id) return
    byId.set(lot.id, { id: lot.id, lotKey: lot.lotKey })
  })

  return byId
}

const resolveDatasetLotReference = (args: {
  farmId: string
  productionUnitId: string
  lotId?: string | null
  lot: string | null | undefined
  lotsByContext: Map<string, Array<{ id: string; lotKey: string }>>
  lotsById: Map<string, { id: string; lotKey: string }>
}): { lotId: string; lot: string; groupKey: string } => {
  const rawLotId = String(args.lotId ?? '').trim()
  if (rawLotId) {
    const matchedById = args.lotsById.get(rawLotId)
    if (matchedById) {
      return {
        lotId: matchedById.id,
        lot: matchedById.lotKey,
        groupKey: `id:${matchedById.id}`
      }
    }

    return {
      lotId: rawLotId,
      lot: String(args.lot ?? '').trim(),
      groupKey: `id:${rawLotId}`
    }
  }

  const rawLot = String(args.lot ?? '').trim()
  if (!rawLot) {
    return { lotId: '', lot: '', groupKey: 'label:' }
  }

  const contextLots = args.lotsByContext.get(buildLotContextKey(args.farmId, args.productionUnitId)) ?? []
  const resolvedLot = findAvailableLotMatch(rawLot, contextLots)
  if (resolvedLot.status === 'matched') {
    return {
      lotId: resolvedLot.match.id,
      lot: resolvedLot.match.lotKey,
      groupKey: `id:${resolvedLot.match.id}`
    }
  }

  return {
    lotId: '',
    lot: rawLot,
    groupKey: `label:${rawLot}`
  }
}

const toWaterRow = (row: WaterDataRow, sourceKey: string, context?: WaterRowContext): ReportRow => ({
  sourceKey,
  id: row.id,
  farmId: context?.farmId ?? '',
  productionUnitId: context?.productionUnitId ?? '',
  lotId: row.lotId ?? '',
  date: row.date,
  lot: row.lot,
  temperature: row.temperature,
  ph: row.ph,
  dissolvedOxygen: row.dissolvedOxygen,
  dissolvedOxygenSaturation: row.dissolvedOxygenSaturation,
  manualDissolvedOxygen: row.manualDissolvedOxygen,
  manualDissolvedOxygenSaturation: row.manualDissolvedOxygenSaturation,
  salinity: row.salinity,
  flowLpm: readFlowLpm(row),
  notes: row.notes
})

const isWaterDatasetBaseKey = (baseKey: string): boolean => {
  if (baseKey.startsWith('water-v2-')) return true
  if (!baseKey.startsWith('water-')) return false
  if (baseKey.startsWith('water-migrated-')) return false
  if (baseKey.startsWith('water-backup-')) return false
  if (baseKey.startsWith('water-selected-')) return false
  return true
}

const listTenantLocalStorageBaseKeys = (tenantKey: string): string[] => {
  const baseKeys = new Set<string>()
  const scopedSuffix = `::${tenantKey}`

  // findings #75: water tables may live in the IndexedDB-backed bulk store.
  for (const key of listStorageKeys()) {
    if (!key) continue

    if (key.endsWith(scopedSuffix)) {
      baseKeys.add(key.slice(0, -scopedSuffix.length))
      continue
    }

    // Default tenant historically wrote some keys without the ::tenant suffix.
    if (tenantKey === 'default' && !key.includes('::')) {
      baseKeys.add(key)
    }
  }

  return Array.from(baseKeys)
}

const BASE_DATASETS: ReportDataset[] = [
  {
    key: 'tasks',
    label: 'Tasks',
    columns: [
      { key: 'title', label: 'Title' },
      { key: 'completed', label: 'Completed' },
      { key: 'dueAt', label: 'Due at' },
      { key: 'tags', label: 'Tags' },
      { key: 'updatedAt', label: 'Updated at' },
      { key: 'id', label: 'ID' },
      { key: 'description', label: 'Description' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const tasks = await dexieTaskRepo.list()
      return tasks.map(toTaskRow)
    }
  },
  {
    key: 'inventory',
    label: 'Inventory',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'unit', label: 'Unit' },
      { key: 'receivedOn', label: 'Received on' },
      { key: 'expiresOn', label: 'Expires on' },
      { key: 'onHand', label: 'On hand' },
      { key: 'reorderPoint', label: 'Reorder point' },
      { key: 'usageFeedType', label: 'Usage feed type' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'updatedAt', label: 'Updated at' },
      { key: 'createdAt', label: 'Created at' },
      { key: 'id', label: 'ID' },
      { key: 'notes', label: 'Notes' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const items = await dexieInventoryItemRepo.list()
      return items.map(toInventoryRow)
    }
  },
  {
    key: 'feed',
    label: 'Feed',
    columns: [
      { key: 'sourceKey', label: 'Source key' },
      { key: 'date', label: 'Date' },
      { key: 'lot', label: 'Lot' },
      { key: 'stage', label: 'Stage' },
      { key: 'feedKg', label: 'Feed (kg)' },
      { key: 'fcr', label: 'FCR' },
      { key: 'feedProteinPct', label: 'Feed protein (%)' },
      { key: 'temperatureC', label: 'Temperature (C)' },
      { key: 'salinityPpt', label: 'Salinity (ppt)' },
      { key: 'weight', label: 'Weight' },
      { key: 'fishStock', label: 'Fish stock' },
      { key: 'fishStockDerived', label: 'Fish stock (derived)', derived: true },
      { key: 'dailyMortality', label: 'Daily mortality' },
      { key: 'mortalityRatePct', label: 'Mortality rate (%)', derived: true },
      { key: 'id', label: 'ID' }
    ],
    fetchRows: async () => {
      // T3-C-02c (2026-07-13): consume overlay-composed rows so the
      // report emits the same weight / mortality / stock / harvest
      // values that Biomass and Datatable render, not the raw stored
      // cache. See docstring on `loadOverlaidProductionEntitiesBySourceKey`.
      const entries = await loadOverlaidProductionEntitiesBySourceKey()

      // `fishStock` only carries a value on the exact-date row a stock
      // count landed on (same convention Biomass/Datatable use — I-6);
      // `fishStockDerived` fills every row via `buildFishStockDerivedByRowId`
      // (shared with `productionDatatable`, see its own doc for why).
      // `mortalityRatePct` reuses the same canonical `dailyMortalityRate.ts`
      // rate calc Database's own "Mortalidad diaria %" column uses (v2.2
      // #35/#36's receiving-day-aware fix), rather than a sixth duplicate
      // implementation of "rate mortality against known stock."
      const derivedStockByRowId = buildFishStockDerivedByRowId(entries)
      const mortalityRateByRowId = computeDailyMortalityRateByRowId(entries.map(({ row }) => row))

      const output: ReportRow[] = []
      entries.forEach(({ sourceKey, row }) => {
        // Only keep rows that actually have a feed entry.
        const feedValue = (row as Record<string, unknown>).feedKg
        const hasFeed = typeof feedValue === 'string'
          ? feedValue.trim().length > 0
          : typeof feedValue === 'number'

        if (!hasFeed) return
        // `undefined` (row's lot key excluded above) and `null` (genuinely
        // no stock signal yet) both mean "unknown" — a confirmed `0` from a
        // depleted lot must pass through untouched (v2.2 findings.md #31).
        const derived = derivedStockByRowId.get(row.id) ?? null
        const rawRatePct = mortalityRateByRowId.get(row.id)?.ratePct ?? null
        output.push({
          ...toProductionRow(row, sourceKey),
          fishStockDerived: derived,
          mortalityRatePct: rawRatePct === null ? null : Number(rawRatePct.toFixed(3))
        })
      })

      return output
    }
  },
  {
    key: 'feedEvents',
    label: 'Feed events (Actual fed)',
    columns: [
      { key: 'recordedAt', label: 'Recorded at' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'feedKg', label: 'Feed (kg)' },
      { key: 'feedType', label: 'Feed type' },
      { key: 'sfrPercent', label: 'SFR (%)' },
      { key: 'biomassKg', label: 'Biomass (kg)' },
      { key: 'fcr', label: 'FCR' },
      { key: 'notes', label: 'Notes' },
      { key: 'mixName', label: 'Mix' },
      { key: 'feedLotNumber', label: 'Feed lot number' },
      { key: 'feedingResponse', label: 'Feeding response' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const [logs, lots] = await Promise.all([
        currentRecords(dexieFeedLogRepo.list()),
        dexieLotRepo.list()
      ])
      const lotsByContext = buildLotsByContext(lots)
      const lotsById = buildLotsById(lots)
      return logs.map((log) => toFeedEventRow(log, resolveDatasetLotReference({
        farmId: log.farmId,
        productionUnitId: log.productionUnitId,
        lotId: log.lotId,
        lot: log.lot,
        lotsByContext,
        lotsById
      })))
    }
  },
  {
    key: 'feedDailyTotals',
    label: 'Feed daily totals (derived)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'totalFeedKg', label: 'Total feed (kg)' },
      { key: 'eventCount', label: 'Event count' },
      { key: 'feedTypes', label: 'Feed types' },
      { key: 'lastRecordedAt', label: 'Last recorded at' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const [logs, farms, lots] = await Promise.all([
        currentRecords(dexieFeedLogRepo.list()),
        dexieFarmRepo.list().catch(() => []),
        dexieLotRepo.list()
      ])
      const lotsByContext = buildLotsByContext(lots)
      const lotsById = buildLotsById(lots)

      const defaultTimeZone = resolveTimeZone()
      const timeZoneByFarmId = new Map<string, string>()
      farms.forEach((farm) => {
        const id = String((farm as any)?.id ?? '').trim()
        if (!id) return
        timeZoneByFarmId.set(id, resolveTimeZone(getTimeZoneFromFarm(farm)))
      })

      const totals = new Map<string, {
        date: string
        farmId: string
        productionUnitId: string
        lotId: string
        lot: string
        totalFeedKg: number
        eventCount: number
        feedTypes: Set<string>
        lastRecordedAt: string
      }>()

      logs.forEach((log) => {
        const timeZone = timeZoneByFarmId.get(log.farmId) ?? defaultTimeZone
        const isoDate = getZonedIsoDate(log.recordedAt, timeZone)
        if (!isoDate) return
        const resolvedLot = resolveDatasetLotReference({
          farmId: log.farmId,
          productionUnitId: log.productionUnitId,
          lotId: log.lotId,
          lot: log.lot,
          lotsByContext,
          lotsById
        })
        const key = buildFeedDailyTotalKey({
          farmId: log.farmId,
          productionUnitId: log.productionUnitId,
          isoDate,
          lot: resolvedLot.groupKey
        })

        const feedKg = parseDecimal(log.feedKg) ?? 0
        const feedType = (log.feedType ?? '').trim()

        const existing = totals.get(key)
        if (!existing) {
          const set = new Set<string>()
          if (feedType) set.add(feedType)
          totals.set(key, {
            date: isoDate,
            farmId: log.farmId,
            productionUnitId: log.productionUnitId,
            lotId: resolvedLot.lotId,
            lot: resolvedLot.lot,
            totalFeedKg: feedKg,
            eventCount: 1,
            feedTypes: set,
            lastRecordedAt: log.recordedAt
          })
          return
        }

        existing.totalFeedKg += feedKg
        existing.eventCount += 1
        if (feedType) existing.feedTypes.add(feedType)
        if (log.recordedAt > existing.lastRecordedAt) existing.lastRecordedAt = log.recordedAt
      })

      return Array.from(totals.values())
        .sort((a, b) => {
          const byDate = b.date.localeCompare(a.date)
          if (byDate !== 0) return byDate
          const byFarm = a.farmId.localeCompare(b.farmId)
          if (byFarm !== 0) return byFarm
          const byUnit = a.productionUnitId.localeCompare(b.productionUnitId)
          if (byUnit !== 0) return byUnit
          return a.lot.localeCompare(b.lot)
        })
        .map((row) => ({
          date: row.date,
          farmId: row.farmId,
          productionUnitId: row.productionUnitId,
          lotId: row.lotId,
          lot: row.lot,
          totalFeedKg: Number(row.totalFeedKg.toFixed(3)),
          eventCount: row.eventCount,
          feedTypes: Array.from(row.feedTypes).join(', '),
          lastRecordedAt: row.lastRecordedAt
        }))
    }
  },
  {
    key: 'productionDatatable',
    label: 'Production Datatable',
    columns: [
      { key: 'sourceKey', label: 'Source key' },
      { key: 'date', label: 'Date' },
      { key: 'lot', label: 'Lot' },
      { key: 'stage', label: 'Stage' },
      { key: 'weight', label: 'Weight' },
      { key: 'dailyMortality', label: 'Daily mortality' },
      { key: 'fishStock', label: 'Fish stock' },
      // v2.2 findings.md #26: was missing here — `feed` already had it.
      { key: 'fishStockDerived', label: 'Fish stock (derived)', derived: true, stockBased: true },
      { key: 'mortalityRatePct', label: 'Mortality rate (%)', derived: true, stockBased: true },
      { key: 'biomassKgDerived', label: 'Biomass (kg, derived)', derived: true, stockBased: true },
      // v2.2 findings.md #188: on an exact count's day.
      { key: 'countDifference', label: 'Count difference', derived: true },
      { key: 'countDifferencePct', label: 'Count difference (%)', derived: true },
      { key: 'countDifferenceFrom', label: 'Count difference spread from' },
      { key: 'health', label: 'Health' },
      { key: 'healthDetail', label: 'Health detail' },
      ...PRODUCTION_PRESET_COLUMN_KEYS
        .filter((key) => !['date', 'lot', 'stage', 'weight', 'dailyMortality', 'fishStock', 'health', 'healthDetail'].includes(key))
        .map((key) => ({ key, label: key })),
      { key: 'id', label: 'ID' }
    ],
    fetchRows: async (scope) => {
      const output: ReportRow[] = []

      // T3-C-02c (2026-07-13): the `productionDatatable` dataset now
      // ACTUALLY reads through the full WSL / MortalityLog /
      // FishStockLog / HarvestLog / Lot.stage overlay chain — the
      // comment on the `lots` dataset above was previously aspirational
      // (the pre-slice implementation emitted raw entities). See
      // `loadOverlaidProductionEntitiesBySourceKey` for the shared
      // helper.
      const entries = await loadOverlaidProductionEntitiesBySourceKey()
      // v2.2 findings.md #26: unlike `feed`, every row is kept regardless
      // of what event type it has (or lacks) — `feed` filters to
      // feed-bearing rows only, but Datatable shows every production row.
      const derivedStockByRowId = buildFishStockDerivedByRowId(entries)
      // Reuses the same canonical `dailyMortalityRate.ts` rate calc
      // Database's "Mortalidad diaria %" column uses (v2.2 #35/#36's
      // receiving-day-aware fix), not a sixth duplicate implementation.
      const mortalityRateByRowId = computeDailyMortalityRateByRowId(entries.map(({ row }) => row))
      // v2.2 findings.md #188: count differences, and the reconciled stock
      // when the report asks for it (recorded figures never change).
      const reconciliation = buildStockReconciliation({
        rows: entries.map(({ row }) => row),
        derivedStockByRowId,
        fishStockLogs: await currentRecords(dexieFishStockLogRepo.list()).catch(() => [] as FishStockLog[])
      })
      const reconciled = scope?.stockView === 'reconciled'
      // Findings #91 / #92: the same empty day rows Base de datos hides.
      const lotExists = buildLotExists(await dexieLotRepo.list().catch(() => [] as Lot[]))
      entries.forEach(({ sourceKey, row }) => {
        const zone = farmTimeZone(sourceKey.split('::')[0])
        if (isHiddenEmptyDayRow(row as Record<string, unknown>, { today: farmToday(zone), timeZone: zone, lotExists })) return
        // `undefined`/`null` both mean "unknown"; a confirmed `0` from a
        // depleted lot must pass through untouched (v2.2 findings.md #31).
        const recorded = derivedStockByRowId.get(row.id) ?? null
        const derived = reconciled ? (reconciliation.reconciledStockByRowId.get(row.id) ?? recorded) : recorded
        let rawRatePct = mortalityRateByRowId.get(row.id)?.ratePct ?? null
        // Reconciled rate: same deaths over the reconciled stock.
        if (reconciled && rawRatePct !== null && recorded !== null && derived !== null && derived > 0) {
          rawRatePct = (rawRatePct * recorded) / derived
        }
        const countDifference = reconciliation.differenceByRowId.get(row.id)
        const productionRow = toProductionRow(row, sourceKey)
        output.push({
          ...productionRow,
          fishStockDerived: derived,
          mortalityRatePct: rawRatePct === null ? null : Number(rawRatePct.toFixed(3)),
          biomassKgDerived: computeBiomassKgDerived(derived, productionRow.weight),
          countDifference: countDifference ? countDifference.difference : null,
          countDifferencePct: countDifference?.differencePct === null || countDifference === undefined
            ? null
            : Number(countDifference.differencePct.toFixed(2)),
          countDifferenceFrom: countDifference ? countDifference.fromDay : null
        })
      })

      return output
    }
  },
  {
    key: 'water',
    label: 'Water',
    columns: [
      { key: 'sourceKey', label: 'Source key' },
      { key: 'date', label: 'Date' },
      { key: 'lot', label: 'Lot' },
      { key: 'temperature', label: 'Temperature' },
      { key: 'ph', label: 'pH' },
      { key: 'dissolvedOxygen', label: 'Dissolved oxygen' },
      { key: 'dissolvedOxygenSaturation', label: 'DO saturation' },
      { key: 'manualDissolvedOxygen', label: 'Manual DO' },
      { key: 'manualDissolvedOxygenSaturation', label: 'Manual DO saturation' },
      { key: 'salinity', label: 'Salinity' },
      { key: 'flowLpm', label: 'Flow (L/min)' },
      { key: 'notes', label: 'Notes' },
      { key: 'id', label: 'ID' }
    ],
    fetchRows: async () => {
      const output: ReportRow[] = []
      const seenWaterRowIds = new Set<string>()

      const pushWaterRow = (row: WaterDataRow, sourceKey: string, context?: WaterRowContext) => {
        if (seenWaterRowIds.has(row.id)) return
        seenWaterRowIds.add(row.id)
        output.push(toWaterRow(row, sourceKey, context))
      }

      await ensureOfflineReady()
      const farms = await dexieFarmRepo.list() as FarmWithProductionUnits[]

      farms.forEach((farm) => {
        const productionUnits = Array.isArray(farm.productionUnits) ? farm.productionUnits : []
        productionUnits.forEach((unit) => {
          if (!unit || typeof unit.id !== 'string' || !unit.id) return

          const rows = loadWaterTableRows({
            farmId: farm.id,
            productionUnitId: unit.id,
            farmName: typeof farm.name === 'string' ? farm.name : '',
            productionUnitName: typeof unit.name === 'string' && unit.name.trim().length > 0
              ? unit.name
              : unit.id
          })

          withoutDeletedLotRecords(rows, unit.id).forEach((row) => {
            if (typeof row.id !== 'string' || typeof row.date !== 'string') return
            pushWaterRow(row, toSourceKeyFromContext(farm.id, unit.id), {
              farmId: farm.id,
              productionUnitId: unit.id
            })
          })
        })
      })

      const tenantKey = getActiveTenantKey()
      const baseKeys = listTenantLocalStorageBaseKeys(tenantKey)

      baseKeys.filter(isWaterDatasetBaseKey).forEach((baseKey) => {
        const rows = readWaterJSON<unknown>(baseKey, [])
        if (!Array.isArray(rows)) return

        rows.forEach((candidate) => {
          if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return
          const record = candidate as Partial<WaterDataRow>
          if (typeof record.id !== 'string') return
          if (typeof record.date !== 'string') return
          pushWaterRow(record as WaterDataRow, baseKey)
        })
      })

      return output
    }
  },
  {
    key: 'lots',
    label: 'Lots',
    // v2.2 T3-A-05 (2026-07-06): retired biology cache columns (recordDate,
    // weight, dailyMortality, fishStock, health, healthDetail) — these lived
    // as legacy denormalized snapshots on Lot and are being retired in T3-A-
    // 06. Operators who need real-time daily biology values should use the
    // 'productionDatatable' dataset which reads through the WSL /
    // MortalityLog / FishStockLog overlays.
    columns: [
      { key: 'lotKey', label: 'Lot key' },
      { key: 'stage', label: 'Stage' },
      { key: 'species', label: 'Species' },
      { key: 'stockingDate', label: 'Stocking date' },
      { key: 'harvestDate', label: 'Harvest date' },
      { key: 'biomassKg', label: 'Biomass (kg)' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'updatedAt', label: 'Updated at' },
      { key: 'createdAt', label: 'Created at' },
      { key: 'id', label: 'ID' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const lots = await dexieLotRepo.list()
      return lots.map(toLotRow)
    }
  },
  {
    // v2.2 T3-D-05 (2026-07-13): cost-per-kg dataset. Aggregates
    // `HarvestLog` (SUM harvest kg per lot) with `LotCostEvent` (SUM
    // amountCents per lot, bucketed G/N/H/L/other) into one row per lot,
    // computing cost per kg in the shared currency. See
    // [src/utils/lotCostPerKg.ts](../utils/lotCostPerKg.ts) for the pure
    // derivation + semantics (MIXED currency, harvest-only rows, cost-
    // only rows). Unblocks the operator-visible cost-per-kg feature
    // originally scoped to T3-B-05.
    key: 'lotCostPerKg',
    label: 'Cost per kg (per lot)',
    columns: [
      { key: 'lotKey', label: 'Lot key' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'speciesKey', label: 'Species' },
      { key: 'expectedFcrMin', label: 'Expected FCR min' },
      { key: 'expectedFcrMax', label: 'Expected FCR max' },
      { key: 'expectedFcrRangeLabel', label: 'Expected FCR range' },
      { key: 'totalHarvestKg', label: 'Total harvest (kg)' },
      { key: 'totalHarvestCount', label: 'Total harvest count' },
      { key: 'harvestEventCount', label: 'Harvest event count' },
      { key: 'totalFeedKg', label: 'Total feed (kg)' },
      { key: 'feedEventCount', label: 'Feed event count' },
      { key: 'totalCostsMajor', label: 'Total costs' },
      { key: 'currency', label: 'Currency' },
      { key: 'costEventCount', label: 'Cost event count' },
      { key: 'costPerKgMajor', label: 'Cost per kg' },
      // v2.2 T3-B-06d: display label emphasizes this is the settled
      // (post-harvest) FCR to disambiguate from the Feed screen's
      // planned/SFR-model FCR. Column KEY stays stable so persisted
      // reports don't break.
      // v2.2 findings.md #215: eFCR (the standards' FCR) and bFCR first;
      // the two older ratios keep their keys for saved reports, with
      // labels that say what they divide.
      { key: 'economicFcr', label: 'eFCR (feed ÷ net production)', derived: true },
      { key: 'biologicalFcr', label: 'bFCR (feed ÷ net production + dead fish)', derived: true },
      { key: 'startBiomassKg', label: 'Biomass at start (kg)', derived: true },
      { key: 'netProductionKg', label: 'Net production (kg)', derived: true },
      { key: 'deadBiomassKg', label: 'Biomass of dead fish (kg)', derived: true },
      { key: 'feedConversionRatio', label: 'Feed ÷ harvested kg' },
      // v2.2 T3-B-06e: mid-cycle diagnostic column. Feed to date
      // divided by current estimated biomass (overlay-chain-derived).
      // Suppressed to null on harvested lots (settled column is the
      // truth) or when current biomass can't be derived.
      { key: 'feedConversionRatioInFlight', label: 'Feed to date ÷ current biomass' },
      { key: 'feedConversionRatioInFlightBand', label: 'FCR in-flight band' },
      { key: 'costG', label: 'Costs — Genetics (G)' },
      { key: 'costN', label: 'Costs — Nutrition/feed (N)' },
      { key: 'costH', label: 'Costs — Health (H)' },
      { key: 'costL', label: 'Costs — Labor (L)' },
      { key: 'costOther', label: 'Costs — Other (I/M/A/F/R)' },
      { key: 'sharePctG', label: 'Share % — Genetics' },
      { key: 'sharePctN', label: 'Share % — Nutrition/feed' },
      { key: 'sharePctH', label: 'Share % — Health' },
      { key: 'sharePctL', label: 'Share % — Labor' },
      { key: 'sharePctOther', label: 'Share % — Other' },
      { key: 'convertedCurrency', label: 'Converted currency' },
      { key: 'convertedTotalCostsCents', label: 'Converted total costs (cents)' },
      { key: 'convertedTotalCostsMajor', label: 'Converted total costs' },
      { key: 'convertedCostPerKgMajor', label: 'Converted cost per kg' },
      { key: 'fxConversionStatus', label: 'FX conversion status' },
      { key: 'fxConversionReason', label: 'FX conversion reason' },
      { key: 'fxAsOf', label: 'FX as of' },
      { key: 'fxRoundingPolicyVersion', label: 'FX rounding policy version' },
      { key: 'fxRateSnapshotId', label: 'FX rate snapshot ID' },
      { key: 'fxRateSourceId', label: 'FX rate source ID' },
      { key: 'fxRateSourceVersion', label: 'FX rate source version' },
      { key: 'fxRateAsOf', label: 'FX rate as of' },
      { key: 'fxRateValue', label: 'FX rate value' },
      { key: 'currencyBreakdown', label: 'Currency breakdown (MIXED)' }
    ],
    fetchRows: async (scope, report) => {
      await ensureOfflineReady()
      // v2.2 T3-B-06e: fetch the three Tier A event streams (mortality
      // / weight sample / fish stock) + production rows so
      // `computeCurrentBiomassPerLot` can derive per-lot current biomass
      // via the same overlay chain Feed / Biomass / Dashboard use.
      // Extra Dexie reads are cheap (list()); the helper is pure.
      const [
        harvestLogs,
        costEvents,
        lots,
        feedLogs,
        farms,
        productionEntities,
        mortalityLogs,
        weightSampleLogs,
        fishStockLogs
      ] = await Promise.all([
        currentRecords(dexieHarvestLogRepo.list()),
        currentRecords(dexieLotCostEventRepo.list()),
        dexieLotRepo.list(),
        currentRecords(dexieFeedLogRepo.list()),
        dexieFarmRepo.list().catch(() => []),
        dexieProductionTableRepo.listAllEntities().catch(() => []),
        currentRecords(dexieMortalityLogRepo.list()).catch(() => []),
        currentRecords(dexieWeightSampleLogRepo.list()).catch(() => []),
        currentRecords(dexieFishStockLogRepo.list()).catch(() => [])
      ])
      // A1.1 fix (docs/architecture-concerns.md §A1.1, Hallazgo D4): fetch
      // every LotEvent touching any lot in the roster so
      // `buildLotCostPerKgRows`'s `(farmId, lotKey)` fallback index can
      // also resolve legacy rows recorded under a lot's PRE-transfer
      // farmId. Best-effort — an empty/failed fetch just falls back to
      // the prior current-farmId-only behavior.
      const lotEvents = await dexieLotEventRepo
        .listForLotIds(lots.map((lot) => lot.id))
        .catch(() => [])
      // Fase 3 (docs/tier-a-merge-consumer-mapping.md §2.6): fetch
      // `LotLineageProjection` rows for every farm touched by the roster
      // so `buildLotCostPerKgRows`'s memberLotId→lotId remap can resolve
      // `LotCostEvent`s recorded against a lot absorbed by a cross-family
      // merge. Best-effort per farm, offline-safe (fails open to []) —
      // matches the lotEvents fetch above.
      const lineageFarmIds = Array.from(
        new Set(lots.map((lot) => lot.farmId).filter((farmId): farmId is string => Boolean(farmId)))
      )
      const lineageRows = (
        await Promise.all(lineageFarmIds.map((farmId) => lotApi.lineageForFarm(farmId).catch(() => [])))
      ).flat()
      // v2.2 T3-B-06c: convert the scope's calendar-day fromDate/toDate
      // (YYYY-MM-DD) into an inclusive-inclusive UTC instant window so
      // `buildLotCostPerKgRows` can trim its input event streams by
      // `recordedAt` before rolling them up. Semantics match the shared
      // `applyReportScope` post-processor in reportScope.ts (from = start
      // of day 00:00:00Z, to = end of day 23:59:59.999Z). Empty scope or
      // missing bounds → lifetime (unchanged pre-06c behavior).
      //
      // v2.2 T3-B-06f: `scope.toDateMode === 'today'` resolves `toDate`
      // at fetch time to today's YYYY-MM-DD in the farm's local
      // timezone (falls back to the browser timezone when the scope
      // spans multiple farms / no farm). Pure resolver `resolveScopeToDate`
      // is unit-tested with an injected clock.
      const fromDate = scope?.fromDate?.trim()
      const singleFarmId = scope?.farmIds?.length === 1 ? scope.farmIds[0] : undefined
      const scopedFarm = singleFarmId ? farms.find((farm) => farm.id === singleFarmId) : undefined
      const timeZone = resolveTimeZone(getTimeZoneFromFarm(scopedFarm))
      const resolvedToDate = resolveScopeToDate(scope, { timeZone })
      const toDate = resolvedToDate.trim()
      const window = (fromDate || toDate)
        ? {
            fromRecordedAt: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
            toRecordedAt: toDate ? `${toDate}T23:59:59.999Z` : undefined
          }
        : undefined
      // v2.2 T3-B-06e: derive current biomass per lot from the overlay
      // chain, honoring the same to-date cutoff as the roll-up so
      // as-of / historical reports carry a consistent temporal frame.
      // The helper returns an empty map when there's no data (unharvested
      // lot with no signal → in-flight column is null, matches the
      // guard inside `computeInFlightFcr`).
      const currentBiomassByLotId = computeCurrentBiomassPerLot({
        productionRows: productionEntities as unknown as BiomassDataRow[],
        mortalityLogs,
        weightSampleLogs,
        fishStockLogs,
        toRecordedAt: toDate || undefined
      })
      // v2.2 findings.md #215: inputs for eFCR / bFCR. With a start date
      // the start is the biomass at the end of the day before; a lot with
      // no figure then started inside the period (its stocked biomass).
      const fcrInputsByLotId = buildLotFcrInputs({
        weightSampleLogs,
        mortalityLogs,
        fishStockLogs,
        fromDay: fromDate || undefined,
        toDay: toDate || undefined
      })
      const startBiomassByLotId = fromDate
        ? computeCurrentBiomassPerLot({
            productionRows: productionEntities as unknown as BiomassDataRow[],
            mortalityLogs,
            weightSampleLogs,
            fishStockLogs,
            toRecordedAt: addDays(fromDate, -1)
          })
        : undefined
      const rows = buildLotCostPerKgRows({
        harvestLogs,
        costEvents,
        lots,
        feedLogs,
        window,
        currentBiomassByLotId,
        lotEvents,
        lineageRows,
        fcrInputsByLotId,
        startBiomassByLotId
      })
      const fxRows: LotCostPerKgFxRow[] = report?.fx
        ? await applyFxConversionToLotCostPerKgRows(rows, {
            targetCurrency: report.fx.targetCurrency,
            asOf: report.fx.asOf,
            findRate: async ({ baseCurrency, quoteCurrency, asOf }) => {
              try {
                return await api.fxRateLogs.latest({ baseCurrency, quoteCurrency, asOf })
              } catch (error) {
                if (error instanceof Error && 'status' in error && error.status === 404) {
                  return undefined
                }
                throw error
              }
            }
          })
        : rows.map((row) => ({
            ...row,
            convertedCurrency: null,
            convertedTotalCostsCents: null,
            convertedTotalCostsMajor: null,
            convertedCostPerKgMajor: null,
            fxConversionMetadata: null
          }))
      // Flatten the nested `costByCategoryMajor` / `categorySharePct` maps
      // into top-level scalar columns so the Reports v1 pivot / CSV
      // export handles them without special-casing nested objects.
      return fxRows.map((row) => ({
        lotKey: row.lotKey,
        lotId: row.lotId,
        farmId: row.farmId,
        productionUnitId: row.productionUnitId,
        speciesKey: row.speciesKey,
        expectedFcrMin: row.expectedFcrMin,
        expectedFcrMax: row.expectedFcrMax,
        expectedFcrRangeLabel: row.expectedFcrRangeLabel,
        totalHarvestKg: row.totalHarvestKg,
        totalHarvestCount: row.totalHarvestCount,
        harvestEventCount: row.harvestEventCount,
        totalFeedKg: row.totalFeedKg,
        feedEventCount: row.feedEventCount,
        totalCostsMajor: row.totalCostsMajor,
        totalCostsCents: row.totalCostsCents,
        currency: row.currency,
        costEventCount: row.costEventCount,
        costPerKgMajor: row.costPerKgMajor,
        economicFcr: row.economicFcr,
        biologicalFcr: row.biologicalFcr,
        startBiomassKg: row.startBiomassKg,
        netProductionKg: row.netProductionKg,
        deadBiomassKg: row.deadBiomassKg,
        feedConversionRatio: row.feedConversionRatio,
        feedConversionRatioInFlight: row.feedConversionRatioInFlight,
        feedConversionRatioInFlightBand: row.feedConversionRatioInFlightBand,
        costG: row.costByCategoryMajor.G,
        costN: row.costByCategoryMajor.N,
        costH: row.costByCategoryMajor.H,
        costL: row.costByCategoryMajor.L,
        costOther: row.costByCategoryMajor.other,
        sharePctG: row.categorySharePct.G,
        sharePctN: row.categorySharePct.N,
        sharePctH: row.categorySharePct.H,
        sharePctL: row.categorySharePct.L,
        sharePctOther: row.categorySharePct.other,
        currencyBreakdown: row.currencyBreakdown,
        convertedCurrency: row.convertedCurrency,
        convertedTotalCostsCents: row.convertedTotalCostsCents,
        convertedTotalCostsMajor: row.convertedTotalCostsMajor,
        convertedCostPerKgMajor: row.convertedCostPerKgMajor,
        fxConversionStatus: row.fxConversionMetadata?.status ?? null,
        fxConversionReason: row.fxConversionMetadata?.status === 'unavailable'
          ? row.fxConversionMetadata.reason
          : null,
        fxAsOf: row.fxConversionMetadata?.asOf ?? null,
        fxRoundingPolicyVersion: row.fxConversionMetadata?.roundingPolicyVersion ?? null,
        fxRateSnapshotId: row.fxConversionMetadata?.status === 'converted'
          ? row.fxConversionMetadata.rate.rateSnapshotId
          : null,
        fxRateSourceId: row.fxConversionMetadata?.status === 'converted'
          ? row.fxConversionMetadata.rate.rateSourceId
          : null,
        fxRateSourceVersion: row.fxConversionMetadata?.status === 'converted'
          ? row.fxConversionMetadata.rate.rateSourceVersion
          : null,
        fxRateAsOf: row.fxConversionMetadata?.status === 'converted'
          ? row.fxConversionMetadata.rate.rateAsOf
          : null,
        fxRateValue: row.fxConversionMetadata?.status === 'converted'
          ? row.fxConversionMetadata.rate.rateValue
          : null
      }))
    }
  },
  {
    // PRD `weightsample-individual-length-v1.md` §3 item 8 / §5, Stage 1
    // Alternative A: CV and Fulton's K are computed read-side only, here,
    // from raw WeightSampleLog rows — never persisted, never shown or
    // computed on the Crew Mode capture screen (Charter O-1, O-3).
    key: 'weightSampleIndividual',
    label: 'Individual weight/length samples (CV, Fulton K)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'readingCount', label: 'Individual readings' },
      { key: 'meanWeightGrams', label: 'Mean weight (g)' },
      { key: 'weightCvPercent', label: 'Weight CV (%)' },
      { key: 'lengthReadingCount', label: 'Readings with length' },
      { key: 'meanLengthMm', label: 'Mean length (mm)' },
      { key: 'meanFultonK', label: "Mean Fulton's K" }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const [weightSampleLogs, farms, lots] = await Promise.all([
        currentRecords(dexieWeightSampleLogRepo.list()).catch(() => []),
        dexieFarmRepo.list().catch(() => []),
        dexieLotRepo.list()
      ])

      const lotsByContext = buildLotsByContext(lots)
      const lotsById = buildLotsById(lots)

      const defaultTimeZone = resolveTimeZone()
      const timeZoneByFarmId = new Map<string, string>()
      farms.forEach((farm) => {
        const id = String((farm as any)?.id ?? '').trim()
        if (!id) return
        timeZoneByFarmId.set(id, resolveTimeZone(getTimeZoneFromFarm(farm)))
      })

      type WeightSampleIndividualGroup = {
        date: string
        farmId: string
        productionUnitId: string
        lotId: string
        lot: string
        weights: number[]
        readings: IndividualSampleReading[]
      }

      const groups = new Map<string, WeightSampleIndividualGroup>()

      weightSampleLogs.forEach((log) => {
        // PRD §5 data identity: a row with sampleCount === 1 represents
        // exactly one weighed individual (PR 2/3's capture flow always
        // writes this). A legacy mean-of-N row (sampleCount undefined or
        // > 1) is excluded here by construction — no new metadata flag,
        // no risk of an aggregate value corrupting the variance figure.
        if (log.sampleCount !== 1) return

        const weightGrams = parseDecimal(log.weightGrams)
        if (weightGrams === null || weightGrams <= 0) return

        const timeZone = timeZoneByFarmId.get(log.farmId) ?? defaultTimeZone
        const isoDate = getZonedIsoDate(log.recordedAt, timeZone)
        if (!isoDate) return

        const resolvedLot = resolveDatasetLotReference({
          farmId: log.farmId,
          productionUnitId: log.productionUnitId,
          lotId: log.lotId,
          lot: log.lot,
          lotsByContext,
          lotsById
        })

        const key = `${log.farmId}::${log.productionUnitId}::${isoDate}::${resolvedLot.groupKey}`
        const lengthMmValue = parseDecimal(log.lengthMm)
        const lengthMm = lengthMmValue !== null && lengthMmValue > 0 ? lengthMmValue : undefined

        const existing = groups.get(key)
        if (!existing) {
          groups.set(key, {
            date: isoDate,
            farmId: log.farmId,
            productionUnitId: log.productionUnitId,
            lotId: resolvedLot.lotId,
            lot: resolvedLot.lot,
            weights: [weightGrams],
            readings: [{ weightGrams, lengthMm }]
          })
          return
        }

        existing.weights.push(weightGrams)
        existing.readings.push({ weightGrams, lengthMm })
      })

      return Array.from(groups.values())
        .sort((a, b) => {
          const byDate = b.date.localeCompare(a.date)
          if (byDate !== 0) return byDate
          const byFarm = a.farmId.localeCompare(b.farmId)
          if (byFarm !== 0) return byFarm
          return a.lot.localeCompare(b.lot)
        })
        .map((group) => {
          const lengthReadings = group.readings.filter((reading) => typeof reading.lengthMm === 'number')
          const meanWeightGrams = group.weights.reduce((sum, value) => sum + value, 0) / group.weights.length
          const meanLengthMm = lengthReadings.length > 0
            ? lengthReadings.reduce((sum, reading) => sum + (reading.lengthMm as number), 0) / lengthReadings.length
            : null
          const weightCv = computeWeightCvPercent(group.weights)
          const meanFultonK = computeMeanFultonK(group.readings)

          return {
            date: group.date,
            farmId: group.farmId,
            productionUnitId: group.productionUnitId,
            lotId: group.lotId,
            lot: group.lot,
            readingCount: group.weights.length,
            meanWeightGrams: Number(meanWeightGrams.toFixed(3)),
            weightCvPercent: weightCv === null ? null : Number(weightCv.toFixed(2)),
            lengthReadingCount: lengthReadings.length,
            meanLengthMm: meanLengthMm === null ? null : Number(meanLengthMm.toFixed(2)),
            meanFultonK: meanFultonK === null ? null : Number(meanFultonK.toFixed(4))
          }
        })
    }
  },
  {
    // PRD `docs/reports-panel-fcr-sgr-sfr-timeseries-prd.md` (item c):
    // weekly, per-lot SGR / FCR (planned) / SFR, computed read-side only
    // here, never persisted. `src/utils/lotGrowthFormulas.ts` is the
    // shared source of truth for the SGR/FCR formulas (also used by
    // Feed.tsx) so the two surfaces can never drift; SFR reuses the same
    // already-shared `resolveEffectiveSfrMatrix` + `interpolateFromMatrix`
    // primitives Feed.tsx already relies on (biomassKg here is the naive
    // fishStock*weight/1000 from each week's own overlay-corrected row —
    // see the comment at its computation below for why this deliberately
    // skips the richer as-of-date resolver `lotCostPerKg` uses).
    // Deliberately the model-predicted "FCR (planned)" metric (PRD
    // decision 1) — NOT `lotCostPerKg`'s cost-accounting FCR, which is a
    // different metric on a different, non-weekly grain (see that
    // dataset's own comment above).
    key: 'lotGrowthPerformance',
    label: 'Lot growth performance (SGR, FCR planned, SFR — weekly)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'week', label: 'Week' },
      { key: 'weekDisplay', label: 'Week display' },
      { key: 'sgrPercent', label: 'SGR (%/day)', derived: true },
      { key: 'sgrClassicPercent', label: 'SGR classic (100·ln, %/day)', derived: true },
      { key: 'sgrDays', label: 'Days between samples' },
      { key: 'meanWeightGrams', label: 'Mean weight (g)' },
      { key: 'biomassKg', label: 'Biomass (kg)', derived: true, stockBased: true },
      { key: 'fcrPlanned', label: 'FCR (planned)', stockBased: true },
      { key: 'sfrPercent', label: 'SFR (%)' }
    ],
    fetchRows: async (scope) => {
      await ensureOfflineReady()

      const [
        overlaidEntries,
        lots,
        farms
      ] = await Promise.all([
        loadOverlaidProductionEntitiesBySourceKey(),
        dexieLotRepo.list().catch(() => []),
        dexieFarmRepo.list().catch(() => []) as Promise<FarmWithProductionUnits[]>
      ])

      const lotsByContext = buildLotsByContext(lots)
      const lotsById = buildLotsById(lots)
      const fullLotsById = new Map(lots.map((lot) => [lot.id, lot]))
      const farmsById = new Map(farms.map((farm) => [farm.id, farm]))

      const defaultTimeZone = resolveTimeZone()
      const timeZoneByFarmId = new Map<string, string>()
      const speciesByFarmId = new Map<string, string>()
      farms.forEach((farm) => {
        const id = String(farm?.id ?? '').trim()
        if (!id) return
        timeZoneByFarmId.set(id, resolveTimeZone(getTimeZoneFromFarm(farm)))
        speciesByFarmId.set(id, resolveFarmProductionSpeciesKey(farm) ?? 'global')
      })

      // Water rows, per (farmId, productionUnitId) — same lookup shape the
      // `water` dataset above already builds.
      const waterRowsBySourceKey = new Map<string, WaterDataRow[]>()
      farms.forEach((farm) => {
        const productionUnits = Array.isArray(farm.productionUnits) ? farm.productionUnits : []
        productionUnits.forEach((unit) => {
          if (!unit || typeof unit.id !== 'string' || !unit.id) return
          const rows = loadWaterTableRows({
            farmId: farm.id,
            productionUnitId: unit.id,
            farmName: typeof farm.name === 'string' ? farm.name : '',
            productionUnitName: typeof unit.name === 'string' && unit.name.trim().length > 0
              ? unit.name
              : unit.id
          })
          waterRowsBySourceKey.set(toSourceKeyFromContext(farm.id, unit.id), rows)
        })
      })

      type WeekBucket = {
        farmId: string
        productionUnitId: string
        lotId: string
        lot: string
        groupKey: string
        week: string
        rowDate: string
        // Latest-dated row of the week: its mortality-aware end-of-day stock
        // is the week's stock (v2.2 findings.md #214).
        rowId: string
        weight: number
        // Date of the week's latest weight sample (SGR uses the real days
        // between two samples, findings #213).
        weightDate: string
        // `null` = no stock signal yet for this lot; a number, including
        // `0`, is a real figure a fully depleted lot must be able to report
        // (v2.2 findings.md #31).
        stock: number | null
      }

      // Distinguishes "field blank" (null, carry forward) from a genuine
      // numeric reading including `0` — `parseFloat(x) || 0` collapses both
      // into `0`, which is exactly what made a depleted lot's stock un-
      // reportable as zero (v2.2 findings.md #31).
      const parseStockField = (value: unknown): number | null => {
        if (typeof value !== 'string' && typeof value !== 'number') return null
        const trimmed = String(value).trim()
        if (!trimmed) return null
        const parsed = Number.parseFloat(trimmed)
        return Number.isFinite(parsed) ? parsed : null
      }

      // Group overlay-corrected production rows into (farm, PU, lot, week)
      // buckets. `rowDate` tracks the latest-dated row in the week (used
      // for display and for picking the nearest water-temperature
      // reading), but `weight`/`stock` are each resolved independently as
      // the latest-dated row *carrying that field* within the week — not
      // simply whichever row is latest overall. A week can hold more than
      // one raw row (e.g. a fish-stock count on Tuesday, a later
      // no-op/status row on Wednesday); picking one "winning" row and
      // discarding the rest silently drops a fish-stock count whenever a
      // blank-stock row happens to be dated later in the same week —
      // exactly what happened to a real Sur A lot whose only-ever stock
      // count shared an ISO week with a later blank-stock row. Mirrors
      // Feed.tsx's own kpiMap construction (selectLatestDatedRow per
      // canonical week) for `rowDate`, but resolves weight/stock the same
      // independent-latest-non-blank way `computeCurrentBiomassPerLot`
      // already does across full history — just scoped to one week here.
      // v2.2 findings.md #214: the week's stock is the same mortality-aware
      // end-of-day stock Base de datos shows (`buildFishStockDerivedByRowId`,
      // one cheap pass over all rows), not the last count carried forward —
      // otherwise deaths between counts never reach biomass and a new count
      // reads as a jump, and Alimento (which uses the mortality-aware stock)
      // shows a different biomass / FCR (planificado) for the same week.
      const recordedStockByRowId = buildFishStockDerivedByRowId(overlaidEntries)
      // v2.2 findings.md #188: "Stock reconciliado" swaps in the reconciled
      // stock (biomass, FCR planificado); SGR never uses stock.
      const derivedStockByRowId = scope?.stockView === 'reconciled'
        ? buildStockReconciliation({
            rows: overlaidEntries.map(({ row }) => row),
            derivedStockByRowId: recordedStockByRowId,
            fishStockLogs: await currentRecords(dexieFishStockLogRepo.list()).catch(() => [] as FishStockLog[])
          }).reconciledStockByRowId
        : recordedStockByRowId

      type WeekBucketBuilder = WeekBucket & { stockDate: string }
      const buckets = new Map<string, WeekBucketBuilder>()

      overlaidEntries.forEach(({ sourceKey, row }) => {
        if (!row.date) return
        const [farmId, productionUnitId] = sourceKey.split('::')
        if (!farmId || !productionUnitId) return

        const timeZone = timeZoneByFarmId.get(farmId) ?? defaultTimeZone
        const week = getZonedWeekKey(row.date, timeZone)
        if (!week) return

        const resolvedLot = resolveDatasetLotReference({
          farmId,
          productionUnitId,
          lotId: row.lotId,
          lot: row.lot,
          lotsByContext,
          lotsById
        })
        if (!resolvedLot.groupKey || resolvedLot.groupKey === 'label:') return

        const bucketKey = `${farmId}::${productionUnitId}::${resolvedLot.groupKey}::${week}`
        const rowWeight = parseFloat(row.weight) || 0
        const rowStock = parseStockField(row.fishStock)

        const existing = buckets.get(bucketKey)
        if (!existing) {
          buckets.set(bucketKey, {
            farmId,
            productionUnitId,
            lotId: resolvedLot.lotId,
            lot: resolvedLot.lot,
            groupKey: resolvedLot.groupKey,
            week,
            rowDate: row.date,
            rowId: row.id,
            weight: rowWeight,
            weightDate: rowWeight > 0 ? row.date : '',
            stock: rowStock,
            stockDate: rowStock !== null ? row.date : ''
          })
          return
        }

        if (row.date.localeCompare(existing.rowDate) > 0) {
          existing.rowDate = row.date
          existing.rowId = row.id
        }
        if (rowWeight > 0 && row.date.localeCompare(existing.weightDate) > 0) {
          existing.weight = rowWeight
          existing.weightDate = row.date
        }
        if (rowStock !== null && row.date.localeCompare(existing.stockDate) > 0) {
          existing.stock = rowStock
          existing.stockDate = row.date
        }
      })

      const byLot = new Map<string, WeekBucket[]>()
      buckets.forEach((bucket) => {
        const lotKey = `${bucket.farmId}::${bucket.productionUnitId}::${bucket.groupKey}`
        const list = byLot.get(lotKey)
        if (list) list.push(bucket)
        else byLot.set(lotKey, [bucket])
      })

      type ResolvedWeek = WeekBucket & {
        effectiveStock: number | null
        biomassKg: number
        sfrPercent?: number
        weeklyFeedKg?: number
      }

      const rows: ReportRow[] = []

      byLot.forEach((weeks) => {
        const sorted = weeks.slice().sort((a, b) => a.week.localeCompare(b.week))
        const first = sorted[0]
        const timeZone = timeZoneByFarmId.get(first.farmId) ?? defaultTimeZone
        const species = speciesByFarmId.get(first.farmId) ?? 'global'
        const farm = farmsById.get(first.farmId)
        const lotEntity = first.lotId ? fullLotsById.get(first.lotId) : undefined
        const matrix = resolveEffectiveSfrMatrix({
          farm: farm as unknown as WizardFarm | undefined,
          lot: lotEntity,
          species: species as ReturnType<typeof resolveFarmProductionSpeciesKey> & string
        })
        const waterRows = waterRowsBySourceKey.get(
          toSourceKeyFromContext(first.farmId, first.productionUnitId)
        ) ?? []

        // Week stock = the mortality-aware end-of-day stock of the week's
        // latest row (findings #214), already carried forward per lot by
        // `buildFishStockDerivedByRowId`; the same-week count is only a
        // fallback for a row the derivation does not cover. A genuine `0`
        // (lot fully depleted) is kept as `0`, not "no signal" (v2.2
        // findings.md #31). Cheap O(n) walk, no per-date resolver.
        const effectiveStocks = carryForwardLastKnownStock(
          sorted,
          (bucket) => derivedStockByRowId.get(bucket.rowId) ?? bucket.stock
        )

        const resolved: ResolvedWeek[] = sorted.map((bucket, index) => {
          // `null` ("no stock signal yet this lot") and a confirmed `0`
          // both correctly zero `biomassKg` below, and the SGR/FCR guards
          // further down already gate on `biomassKg > 0` regardless of
          // which case produced the zero — safe to coalesce here.
          const effectiveStock = effectiveStocks[index] ?? 0

          // biomassKg = stock * weight / 1000, using this week's own
          // weight reading and the mortality-aware stock above.
          // Deliberately NOT `buildFeedWeeklyBiomassResolver`/
          // `computeCurrentBiomassPerLot` here: that resolver re-scans
          // every mortality/weight-sample/fish-stock log for every
          // *distinct* `toRecordedAt` it's asked about, and this dataset
          // asks about one distinct date per (lot, week) — on a tenant
          // with many weeks of history that blocked the main thread for
          // several seconds on Panel load.
          const biomassKg = (effectiveStock * bucket.weight) / 1000

          const nearestWater = selectNearestDatedRow(bucket.rowDate, waterRows)
          let sfrPercent: number | undefined
          let weeklyFeedKg: number | undefined
          if (bucket.weight > 0 && nearestWater?.temperature !== null && nearestWater?.temperature !== undefined) {
            const sfrResult = interpolateFromMatrix(matrix, bucket.weight, nearestWater.temperature, {
              species: species as ReturnType<typeof resolveFarmProductionSpeciesKey> & string
            })
            sfrPercent = sfrResult.sfr
            if (biomassKg > 0) {
              weeklyFeedKg = computeProjectedWeeklyFeedKg(biomassKg, sfrPercent)
            }
          }

          return { ...bucket, effectiveStock: effectiveStocks[index] ?? null, biomassKg, sfrPercent, weeklyFeedKg }
        })

        resolved.forEach((current, index) => {
          const next = resolved[index + 1]
          let sgrPercent: number | undefined
          let fcrPlanned: number | undefined

          // v2.2 findings.md #213: SGR from the mean weight of this week's
          // sample to the next sample of the lot, over the real days between
          // them (never biomass, never a fixed 7 days).
          // A lot confirmed at 0 fish has no mean weight to grow from or to
          // (v2.2 findings.md #31): no SGR across a depleted week.
          const nextSample = current.weight > 0
            ? resolved.slice(index + 1).find((week) => week.weight > 0)
            : undefined
          const depleted = current.effectiveStock === 0 || nextSample?.effectiveStock === 0
          const sgr = nextSample && !depleted
            ? computeSgrFromSamples({
                startWeightG: current.weight,
                startDate: getZonedIsoDate(current.weightDate, timeZone) ?? '',
                endWeightG: nextSample.weight,
                endDate: getZonedIsoDate(nextSample.weightDate, timeZone) ?? ''
              })
            : undefined
          sgrPercent = sgr?.sgrPercent

          if (next) {
            if (current.biomassKg > 0 && next.biomassKg > 0) {
              const weightGainedKg = next.biomassKg - current.biomassKg
              fcrPlanned = computeFcrPlanned(current.weeklyFeedKg ?? 0, weightGainedKg)
            }
          }

          rows.push({
            date: getZonedIsoDate(current.rowDate, timeZone) ?? current.rowDate,
            farmId: current.farmId,
            productionUnitId: current.productionUnitId,
            lotId: current.lotId,
            lot: current.lot,
            week: current.week,
            weekDisplay: formatZonedWeekDisplay(current.rowDate, current.week, timeZone),
            sgrPercent: sgrPercent === undefined ? null : Number(sgrPercent.toFixed(4)),
            sgrClassicPercent: sgr ? Number(sgr.sgrClassicPercent.toFixed(4)) : null,
            sgrDays: sgr ? sgr.days : null,
            meanWeightGrams: current.weight > 0 ? Number(current.weight.toFixed(2)) : null,
            biomassKg: current.biomassKg > 0 ? Number(current.biomassKg.toFixed(3)) : null,
            fcrPlanned: fcrPlanned === undefined ? null : Number(fcrPlanned.toFixed(4)),
            sfrPercent: current.sfrPercent === undefined ? null : Number(current.sfrPercent.toFixed(4))
          })
        })
      })

      return rows.sort((a, b) => {
        const byDate = String(b.date).localeCompare(String(a.date))
        if (byDate !== 0) return byDate
        const byFarm = String(a.farmId).localeCompare(String(b.farmId))
        if (byFarm !== 0) return byFarm
        return String(a.lot).localeCompare(String(b.lot))
      })
    }
  },
  {
    // v2.2 findings #74: mortality recording completeness per lot and week —
    // "días sin registro" next to the mortality figures. Display only; no
    // mortality number changes. See utils/mortalityRecording.ts.
    key: 'mortalityRecording',
    label: 'Mortality recording (days recorded / without record — weekly)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'week', label: 'Week' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'daysInPeriod', label: 'Days in period' },
      { key: 'daysRecorded', label: 'Days recorded' },
      { key: 'daysNoMortality', label: 'Days with no mortality' },
      { key: 'daysNotChecked', label: 'Days not checked' },
      { key: 'daysWithoutRecord', label: 'Days without record' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const [lots, mortalityLogs] = await Promise.all([
        dexieLotRepo.list().catch(() => []),
        currentRecords(dexieMortalityLogRepo.list()).catch(() => [])
      ])
      return buildMortalityRecordingRows({ lots, mortalityLogs }).sort((a, b) => (
        String(b.date).localeCompare(String(a.date)) || String(a.lot).localeCompare(String(b.lot))
      )) as unknown as ReportRow[]
    }
  },
  {
    // Findings #140: mortality by observed cause (Crew Mode
    // `metadata.observedCause`) per lot and week — deaths and share of the
    // week's deaths. See utils/mortalityByCause.ts.
    key: 'mortalityByCause',
    label: 'Mortality by cause (weekly, per lot)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'week', label: 'Week' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'cause', label: 'Cause' },
      { key: 'deaths', label: 'Deaths' },
      { key: 'weekDeaths', label: 'Deaths in the week (all causes)' },
      { key: 'causeSharePct', label: 'Share of the week\'s deaths (%)', derived: true },
      { key: 'causeCode', label: 'Cause code' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const [lots, mortalityLogs] = await Promise.all([
        dexieLotRepo.list().catch(() => []),
        currentRecords(dexieMortalityLogRepo.list()).catch(() => [])
      ])
      // Same language as the screens (main.tsx: the browser's locale).
      const dictionary = resolveBrowserLocale().startsWith('es') ? es : {}
      const causeLabel = (key: TranslationKey): string => dictionary[key] ?? en[key]
      return buildMortalityByCauseRows({ lots, mortalityLogs, causeLabel }) as unknown as ReportRow[]
    }
  },
  {
    // findings #82: one row per Active lot — projected harvest from the TGC
    // growth model, recorded-days mortality (#74) and the SFR matrix.
    key: 'lotProjection',
    label: 'Lot projection (harvest date, biomass, feed to harvest)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'projectionStatus', label: 'Projection status' },
      { key: 'lastSamplingDate', label: 'Last sampling' },
      { key: 'lastSamplingWeightG', label: 'Last sampling weight (g)' },
      { key: 'estimatedWeightTodayG', label: 'Estimated weight today (g)', derived: true },
      { key: 'weightIn30DaysG', label: 'Weight in 30 days (g)', derived: true },
      { key: 'targetWeightG', label: 'Target weight (g)' },
      { key: 'daysToHarvest', label: 'Days to harvest', derived: true },
      { key: 'harvestDate', label: 'Projected harvest date', derived: true },
      { key: 'biomassNowKg', label: 'Biomass now (kg)', derived: true },
      { key: 'stockAtHarvest', label: 'Stock at harvest', derived: true },
      { key: 'biomassAtHarvestKg', label: 'Biomass at harvest (kg)', derived: true },
      { key: 'feedToHarvestKg', label: 'Feed to harvest (kg)', derived: true },
      { key: 'projectedFcr', label: 'Projected FCR', derived: true },
      { key: 'tgc', label: 'TGC' },
      { key: 'tgcSource', label: 'TGC source' },
      { key: 'meanTemperatureC', label: 'Mean temperature (°C)' },
      { key: 'mortalityRatePctPerDay', label: 'Mortality (%/day, recorded days)' },
      { key: 'mortalityDaysRecorded', label: 'Mortality days recorded' },
      { key: 'mortalityWindowDays', label: 'Days in mortality window' },
      { key: 'mortalityCompletenessPct', label: 'Mortality completeness (%)' },
      { key: 'lowConfidence', label: 'Low confidence' }
    ],
    fetchRows: async () => {
      const projections = await loadLotProjections()
      return projections
        .map(({ lotId, lotKey, farmId, productionUnitId, today, projection }) => ({
          date: today,
          farmId,
          productionUnitId,
          lotId,
          lot: lotKey,
          projectionStatus: projection.status,
          lastSamplingDate: projection.lastSamplingDate,
          lastSamplingWeightG: projection.lastSamplingWeightG,
          estimatedWeightTodayG: projection.estimatedWeightTodayG,
          weightIn30DaysG: projection.weightIn30DaysG,
          targetWeightG: projection.targetWeightG,
          daysToHarvest: projection.daysToHarvest,
          harvestDate: projection.harvestDate,
          biomassNowKg: projection.biomassNowKg,
          stockAtHarvest: projection.stockAtHarvest,
          biomassAtHarvestKg: projection.biomassAtHarvestKg,
          feedToHarvestKg: projection.feedToHarvestKg,
          projectedFcr: projection.projectedFcr,
          tgc: projection.tgc,
          tgcSource: projection.tgcSource,
          meanTemperatureC: projection.meanTemperatureC,
          mortalityRatePctPerDay: projection.mortalityRatePctPerDay,
          mortalityDaysRecorded: projection.mortalityDaysRecorded,
          mortalityWindowDays: projection.mortalityWindowDays,
          mortalityCompletenessPct: projection.mortalityCompletenessPct,
          lowConfidence: isLowConfidence(projection) ? 'yes' : 'no'
        }))
        .sort((a, b) => String(a.harvestDate ?? '9999').localeCompare(String(b.harvestDate ?? '9999')) || String(a.lot).localeCompare(String(b.lot)))
    }
  },
  {
    // Hatchery v0 (findings #84): one row per Active egg / alevin lot — TUA,
    // expected and recorded hatch / first feeding, survival per stage
    // (GLOBALG.A.P. 20.05.02), density and flow, egg origin and arrival
    // record (GLOBALG.A.P. 20.01.05 / 20.02.05, BAP Hatchery T4 / 4.7).
    key: 'lotHatchery',
    label: 'Hatchery (eggs and alevins)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'farmId', label: 'Farm ID' },
      { key: 'productionUnitId', label: 'Production unit ID' },
      { key: 'lotId', label: 'Lot ID' },
      { key: 'lot', label: 'Lot' },
      { key: 'lifeStage', label: 'Life stage' },
      { key: 'arrivalDate', label: 'Arrival date' },
      { key: 'eggsReceived', label: 'Eggs received' },
      { key: 'tuaAtArrival', label: 'TUA at arrival' },
      { key: 'tuaToDate', label: 'TUA to date', derived: true },
      { key: 'meanTemperatureC', label: 'Mean temperature (°C)' },
      { key: 'temperatureDaysRecorded', label: 'Days with temperature' },
      { key: 'temperatureDaysBorrowed', label: 'Of these, from another unit of the same arrival or the measuring point' },
      { key: 'hatchTargetTua', label: 'Hatch target (TUA)' },
      { key: 'hatchDate', label: 'Hatch date' },
      { key: 'expectedHatchDate', label: 'Expected hatch date', derived: true },
      { key: 'firstFeedingTargetTua', label: 'First feeding target (TUA)' },
      { key: 'firstFeedingDate', label: 'First feeding date' },
      { key: 'expectedFirstFeedingDate', label: 'Expected first feeding date', derived: true },
      { key: 'eggDeaths', label: 'Dead eggs' },
      { key: 'alevinDeaths', label: 'Dead alevins' },
      { key: 'aliveNow', label: 'Alive now', derived: true },
      { key: 'survivalToHatchPct', label: 'Survival to hatch (%)', derived: true },
      { key: 'survivalToFirstFeedingPct', label: 'Survival to first feeding (%)', derived: true },
      { key: 'survivalToDatePct', label: 'Survival to date (%)', derived: true },
      { key: 'mortalityDaysRecorded', label: 'Mortality days recorded' },
      { key: 'mortalityCompletenessPct', label: 'Mortality completeness (%)' },
      { key: 'perLitre', label: 'Eggs / alevins per litre', derived: true },
      { key: 'flowLpm', label: 'Flow (L/min)' },
      { key: 'flowPerThousandLpm', label: 'Flow per 1,000 (L/min)', derived: true },
      { key: 'eggDiameterMm', label: 'Egg diameter (mm)' },
      { key: 'eggsPerGram', label: 'Eggs per gram' },
      { key: 'eggWeightMg', label: 'Egg weight (mg, approx.)', derived: true },
      { key: 'estimatedFryWeightG', label: 'Fry weight at first feeding (g, approx.)', derived: true },
      { key: 'seedSupplier', label: 'Supplier' },
      { key: 'supplierLot', label: 'Supplier egg lot' },
      { key: 'supplierCertification', label: 'Supplier certification' },
      { key: 'strain', label: 'Strain' },
      { key: 'broodstockGroup', label: 'Broodstock group / origin' },
      { key: 'spawningDate', label: 'Spawning (fertilisation) date' },
      { key: 'stockTraits', label: 'Stock traits' },
      { key: 'healthCertificateNo', label: 'Health certificate' },
      { key: 'healthCertificateDate', label: 'Health certificate date' },
      { key: 'quarantine', label: 'Quarantine' },
      { key: 'disinfectionProduct', label: 'Disinfection product' },
      { key: 'disinfectionPpm', label: 'Disinfection (ppm)' },
      { key: 'disinfectionMinutes', label: 'Disinfection (min)' },
      { key: 'disinfectionDate', label: 'Disinfection date' }
    ],
    fetchRows: async () => {
      const rows = await loadLotHatcheryViews()
      return rows
        .map(({ lotId, lotKey, farmId, productionUnitId, today, lifeStage, arrivalDate, hatchDate, firstFeedingDate, arrival, supplier, strain, view }) => ({
          date: today,
          farmId,
          productionUnitId,
          lotId,
          lot: lotKey,
          lifeStage,
          arrivalDate,
          eggsReceived: view.eggsReceived,
          tuaAtArrival: view.tuaAtArrival,
          tuaToDate: view.tuaToDate,
          meanTemperatureC: view.meanTemperatureC,
          temperatureDaysRecorded: view.temperatureDaysRecorded,
          temperatureDaysBorrowed: view.temperatureDaysBorrowed,
          hatchTargetTua: view.hatch.targetTua,
          hatchDate,
          expectedHatchDate: view.hatch.expectedDate,
          firstFeedingTargetTua: view.firstFeeding.targetTua,
          firstFeedingDate,
          expectedFirstFeedingDate: view.firstFeeding.expectedDate,
          eggDeaths: view.eggDeaths,
          alevinDeaths: view.alevinDeaths,
          aliveNow: view.aliveNow,
          survivalToHatchPct: view.survivalToHatchPct,
          survivalToFirstFeedingPct: view.survivalToFirstFeedingPct,
          survivalToDatePct: view.survivalToDatePct,
          mortalityDaysRecorded: view.mortalityDaysRecorded,
          mortalityCompletenessPct: view.mortalityCompletenessPct,
          perLitre: view.perLitre,
          flowLpm: view.flowLpm,
          flowPerThousandLpm: view.flowPerThousandLpm,
          eggDiameterMm: arrival.eggDiameterMm ?? null,
          eggsPerGram: arrival.eggsPerGram ?? null,
          eggWeightMg: view.eggWeightMg,
          estimatedFryWeightG: view.estimatedFryWeightG,
          seedSupplier: supplier,
          supplierLot: arrival.supplierLot ?? null,
          supplierCertification: arrival.supplierCertification ?? null,
          strain,
          broodstockGroup: arrival.broodstockGroup ?? null,
          spawningDate: arrival.spawningDate ?? null,
          stockTraits: arrival.stockTraits?.join(', ') ?? null,
          healthCertificateNo: arrival.healthCertificateNo ?? null,
          healthCertificateDate: arrival.healthCertificateDate ?? null,
          quarantine: arrival.quarantine === undefined ? null : arrival.quarantine ? 'yes' : 'no',
          disinfectionProduct: arrival.disinfectionProduct ?? null,
          disinfectionPpm: arrival.disinfectionPpm ?? null,
          disinfectionMinutes: arrival.disinfectionMinutes ?? null,
          disinfectionDate: arrival.disinfectionDate ?? null
        }))
        .sort((a, b) => String(a.arrivalDate ?? '').localeCompare(String(b.arrivalDate ?? '')) || String(a.lot).localeCompare(String(b.lot)))
    }
  },
  {
    // v2.2 findings.md #28: one row per mortality record (a crew visit with
    // several causes is one record per cause), for checking each entry: time,
    // cause, note, who recorded it, late entry. Deleted lots stay out, as in
    // every current report (#206); their records are in "Lotes eliminados".
    key: 'mortalityEvents',
    label: 'Mortality records (one row per record)',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'recordedAtLocal', label: 'Date and time' },
      { key: 'lot', label: 'Lot' },
      { key: 'deaths', label: 'Deaths' },
      { key: 'recordKind', label: 'Record' },
      { key: 'cause', label: 'Cause' },
      { key: 'notes', label: 'Note' },
      { key: 'recordedBy', label: 'Recorded by' },
      { key: 'captureSourceLabel', label: 'Recorded on' },
      { key: 'lateEntry', label: 'Late entry' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const [mortalityLogs, actorNames, farms] = await Promise.all([
        currentRecords(dexieMortalityLogRepo.list()).catch(() => [] as MortalityLog[]),
        loadActorNames().catch(() => new Map<string, string>()),
        dexieFarmRepo.list().catch(() => [])
      ])
      const zoneByFarmId = new Map(farms.map((farm) => [farm.id, resolveTimeZone(getTimeZoneFromFarm(farm))] as const))
      const dictionary = resolveBrowserLocale().startsWith('es') ? es : {}
      const label = (key: TranslationKey): string => dictionary[key] ?? en[key]
      return mortalityLogs
        .map((log) => {
          const zone = zoneByFarmId.get(log.farmId) ?? farmTimeZone(log.farmId)
          const metadata = (log.metadata ?? {}) as Record<string, unknown>
          const cause = readMortalityCause(log)
          const kind: TranslationKey = metadata.notChecked === true
            ? 'reports.mortalityEvents.kind.notChecked'
            : metadata.noMortality === true
              ? 'reports.mortalityEvents.kind.noMortality'
              : 'reports.mortalityEvents.kind.deaths'
          return {
            id: log.id,
            farmId: log.farmId,
            productionUnitId: log.productionUnitId,
            lotId: log.lotId ?? null,
            lot: log.lot ?? '',
            recordedAt: log.recordedAt,
            date: getZonedIsoDate(log.recordedAt, zone) ?? log.recordedAt.slice(0, 10),
            recordedAtLocal: farmDateTimeInputValue(log.recordedAt, zone).replace('T', ' '),
            deaths: Number(String(log.mortalityCount ?? '').replace(',', '.')) || 0,
            recordKind: label(kind),
            cause: cause ? label(MORTALITY_CAUSE_LABEL_KEY[cause] ?? NO_CAUSE_LABEL_KEY) : '',
            notes: log.notes ?? '',
            recordedBy: log.capturedByActorId ? actorLabel(actorNames, log.capturedByActorId) : '',
            captureSourceLabel: label(log.captureSource === 'crewMode'
              ? 'reports.mortalityEvents.source.phone'
              : 'reports.mortalityEvents.source.web'),
            lateEntry: metadata.lateEntry === true ? label('reports.mortalityEvents.lateEntry.yes') : ''
          }
        })
        .sort((a, b) => String(b.recordedAt).localeCompare(String(a.recordedAt))) as unknown as ReportRow[]
    }
  },
  {
    // v2.2 findings.md #205: the deleted lots, on purpose (current reports
    // leave them out, #206): when, by whom, restored or not, and how many of
    // each record they hold. "Who" is known for deletions and restores made
    // after this was added; older ones show blank.
    key: 'deletedLots',
    label: 'Deleted lots (audit)',
    columns: [
      { key: 'lot', label: 'Lot' },
      { key: 'createdAtLocal', label: 'Created' },
      { key: 'deletedAtLocal', label: 'Deleted on' },
      { key: 'deletedBy', label: 'Deleted by' },
      { key: 'restored', label: 'Restored' },
      { key: 'restoredAtLocal', label: 'Restored on' },
      { key: 'restoredBy', label: 'Restored by' },
      { key: 'countRecords', label: 'Counts' },
      { key: 'mortalityRecords', label: 'Mortality records' },
      { key: 'deaths', label: 'Deaths' },
      { key: 'weightSamples', label: 'Weight samples' },
      { key: 'feedRecords', label: 'Feed records' },
      { key: 'feedKg', label: 'Feed (kg)' },
      { key: 'harvestKg', label: 'Harvested (kg)' },
      { key: 'waterRecords', label: 'Water readings' },
      { key: 'firstRecordLocal', label: 'First record' },
      { key: 'lastRecordLocal', label: 'Last record' }
    ],
    fetchRows: async () => {
      await ensureOfflineReady()
      const farms = await dexieFarmRepo.list().catch(() => []) as FarmWithProductionUnits[]
      const entries = (await Promise.all(farms.map(async (farm) => {
        // Online: with who and the restored lots; offline: the deleted lots
        // kept on the device (without who / restores).
        const online = await api.lots.deleted(farm.id, { includeRestored: true }).catch(() => null)
        const list = online ?? (await loadDeletedLots(farm.id).catch(() => null)) ?? []
        return list.map((lot) => ({ ...lot, farmId: farm.id }) as DeletedLotAuditEntry)
      }))).flat()
      if (entries.length === 0) return []

      const waterRecords: Array<Record<string, unknown>> = []
      farms.forEach((farm) => {
        ;(Array.isArray(farm.productionUnits) ? farm.productionUnits : []).forEach((unit) => {
          if (!unit || typeof unit.id !== 'string' || !unit.id) return
          loadWaterTableRows({ farmId: farm.id, productionUnitId: unit.id, farmName: String(farm.name ?? ''), productionUnitName: String(unit.name ?? unit.id) })
            .forEach((row) => waterRecords.push({ ...(row as unknown as Record<string, unknown>), productionUnitId: unit.id }))
        })
      })
      // Raw lists on purpose: these are the records current reports leave out.
      const [lots, fishStockLogs, mortalityLogs, weightSampleLogs, feedLogs, harvestLogs, actorNames] = await Promise.all([
        dexieLotRepo.list().catch(() => [] as Lot[]),
        dexieFishStockLogRepo.list().catch(() => []),
        dexieMortalityLogRepo.list().catch(() => []),
        dexieWeightSampleLogRepo.list().catch(() => []),
        dexieFeedLogRepo.list().catch(() => []),
        dexieHarvestLogRepo.list().catch(() => []),
        loadActorNames().catch(() => new Map<string, string>())
      ])
      const counts = buildDeletedLotAuditCounts({
        entries,
        farmLots: lots.map((lot) => ({ id: lot.id, lotKey: lot.lotKey, productionUnitId: lot.productionUnitId, createdAt: lot.createdAt })),
        fishStockLogs,
        mortalityLogs,
        weightSampleLogs,
        feedLogs,
        harvestLogs,
        waterRecords
      })
      const dictionary = resolveBrowserLocale().startsWith('es') ? es : {}
      const label = (key: TranslationKey): string => dictionary[key] ?? en[key]
      return entries
        .map((entry) => {
          const farm = farms.find((candidate) => candidate.id === entry.farmId)
          const zone = farm ? resolveTimeZone(getTimeZoneFromFarm(farm)) : farmTimeZone(entry.farmId)
          const local = (raw: string | null | undefined) => (raw ? farmDateTimeInputValue(raw, zone).replace('T', ' ') : null)
          const lotCounts = counts.get(entry.id)
          return {
            id: entry.id,
            farmId: entry.farmId,
            productionUnitId: entry.productionUnitId ?? null,
            lotId: entry.id,
            lot: entry.lotKey,
            date: entry.deletedAt ? getZonedIsoDate(entry.deletedAt, zone) : null,
            createdAtLocal: local(entry.createdAt),
            deletedAtLocal: local(entry.deletedAt),
            deletedBy: entry.deletedByActorId ? actorLabel(actorNames, entry.deletedByActorId) : '',
            restored: label(entry.restoredAt ? 'reports.deletedLots.restored.yes' : 'reports.deletedLots.restored.no'),
            restoredAtLocal: local(entry.restoredAt),
            restoredBy: entry.restoredByActorId ? actorLabel(actorNames, entry.restoredByActorId) : '',
            countRecords: lotCounts?.countRecords ?? 0,
            mortalityRecords: lotCounts?.mortalityRecords ?? 0,
            deaths: lotCounts?.deaths ?? 0,
            weightSamples: lotCounts?.weightSamples ?? 0,
            feedRecords: lotCounts?.feedRecords ?? 0,
            feedKg: lotCounts ? Number(lotCounts.feedKg.toFixed(3)) : 0,
            harvestKg: lotCounts ? Number(lotCounts.harvestKg.toFixed(3)) : 0,
            waterRecords: lotCounts?.waterRecords ?? 0,
            firstRecordLocal: local(lotCounts?.firstRecordAt),
            lastRecordLocal: local(lotCounts?.lastRecordAt)
          }
        })
        .sort((a, b) => String(b.deletedAtLocal ?? '').localeCompare(String(a.deletedAtLocal ?? ''))) as unknown as ReportRow[]
    }
  }
]

// v2.2 findings.md #67: every lot-scoped dataset also offers Instalación,
// Unidad de producción, "Lote · Unidad" and "Lote (origen)" columns
// (reportLocationColumns.ts), placed right after its lot column, so pivots
// can break down by unit and never merge same-name lots by accident.
const LOT_SCOPED_DATASET_KEYS = new Set<ReportDatasetKey>([
  'feed',
  'feedEvents',
  'feedDailyTotals',
  'productionDatatable',
  'water',
  'lots',
  'lotCostPerKg',
  'weightSampleIndividual',
  'lotGrowthPerformance',
  'mortalityRecording',
  'mortalityByCause',
  'lotProjection',
  'lotHatchery',
  'mortalityEvents',
  'deletedLots'
])

const LOCATION_COLUMNS: DatasetColumn[] = [
  { key: 'farmName', label: 'Facility' },
  { key: 'productionUnitName', label: 'Production unit' },
  { key: 'lotUnit', label: 'Lot · Unit' },
  { key: 'lotOrigin', label: 'Lot (origin)' }
]

const insertLocationColumns = (columns: DatasetColumn[], location: DatasetColumn[]): DatasetColumn[] => {
  const existing = new Set(columns.map((column) => column.key))
  const additions = location.filter((column) => !existing.has(column.key))
  const lotIndex = columns.findIndex((column) => column.key === 'lot' || column.key === 'lotKey')
  if (lotIndex === -1) return [...columns, ...additions]
  return [...columns.slice(0, lotIndex + 1), ...additions, ...columns.slice(lotIndex + 1)]
}

const withLocationColumns = (dataset: ReportDataset): ReportDataset => {
  // Inventory has no lot: findings #220 gives it the facility's name in place of its id.
  const isInventory = dataset.key === 'inventory'
  if (!isInventory && !LOT_SCOPED_DATASET_KEYS.has(dataset.key)) return dataset
  return {
    ...dataset,
    columns: insertLocationColumns(dataset.columns, isInventory ? LOCATION_COLUMNS.slice(0, 1) : LOCATION_COLUMNS),
    fetchRows: async (scope, report) => {
      // Labels are best-effort: a missing farm/lot cache never fails the report.
      const safeList = async <T,>(load: () => Promise<T[]> | undefined): Promise<T[]> => {
        try {
          return (await load()) ?? []
        } catch {
          return []
        }
      }
      const [rows, farms, lots] = await Promise.all([
        dataset.fetchRows(scope, report),
        safeList(() => dexieFarmRepo.list()),
        safeList(() => dexieLotRepo.list())
      ])
      const index = buildLocationIndex(farms as FarmWithProductionUnits[], lots)
      return decorateRowsWithLocation(rows, index) as ReportRow[]
    }
  }
}

export const DATASETS: ReportDataset[] = BASE_DATASETS.map(withLocationColumns)

export type StockCountDifference = CountDifference & {
  farmId: string
  productionUnitId: string
  lot: string
  lotId: string
}

/**
 * Findings #222: the exact counts that "Stock reconciliado" would spread for
 * this scope. The whole lot history counts, whatever the report's dates: a
 * count after the report's last day still changes the days inside it.
 */
export const loadStockCountDifferences = async (scope?: ReportScopeV1 | null): Promise<StockCountDifference[]> => {
  const entries = await loadOverlaidProductionEntitiesBySourceKey()
  const { differenceByRowId } = buildStockReconciliation({
    rows: entries.map(({ row }) => row),
    derivedStockByRowId: buildFishStockDerivedByRowId(entries),
    fishStockLogs: await currentRecords(dexieFishStockLogRepo.list()).catch(() => [] as FishStockLog[])
  })
  const lotScope = scope ? { ...scope, fromDate: '', toDate: '' } : null
  const byLogId = new Map<string, StockCountDifference>()
  entries.forEach(({ sourceKey, row }) => {
    const difference = differenceByRowId.get(row.id)
    if (!difference || difference.difference === 0 || byLogId.has(difference.logId)) return
    const [farmId = '', productionUnitId = ''] = sourceKey.split('::')
    const located = { farmId, productionUnitId, lot: String(row.lot ?? ''), lotId: String(row.lotId ?? '') }
    if (applyReportScope([located], lotScope).length === 0) return
    byLogId.set(difference.logId, { ...difference, ...located })
  })
  return Array.from(byLogId.values()).sort((a, b) => a.day.localeCompare(b.day))
}

export const getDataset = (key: ReportDatasetKey): ReportDataset | undefined => (
  DATASETS.find((dataset) => dataset.key === key)
)
